import { COMPACT_MODAL_QUERY } from '@/components/Modal/ResponsiveModalLayout'
import { setUserIsPremium, setUserIsSuper } from '@/store/slices/user/actions'
import store from '@/store/store'
import { setUser } from '@/store/slices/user/actions'
import * as amplitude from '@amplitude/analytics-browser'
import ApiService from '@services/api'
import Cookies from 'universal-cookie'
import { nanoid } from 'nanoid'
import { UserInfoStorage } from '@/utils/userInfoStorage'
import { sha256Hex } from '@/utils/hash'
import * as Sentry from '@sentry/react'
import { customAmplitude } from '@/utils/customAmplitude'
import { measureChatGPTRegistration, measureChatGPTCheckout } from '@/utils/chatgptAdsConversions'
import { UserInfo } from '@/common/interfaces';
import { DateProvider } from '@/shared/DateProvider'
import api from '@services/api'
import AdminApiService from '@services/AdminApiService'
import { initializeProfitWell } from '@/libs/profitwell'
import { syncOneSignalUser } from '@/libs/onesignal'
import { UserDevice } from '@/shared/sync/dataTypes/UserDevice'
import { SyncManager } from '@/firebase/SyncManager'
import { LocationGeoIPService } from './LocationGeoIPServiceManager'
import { deriveSubscriptionFlagsFromAccessTokenPayload } from '@/utils/subscriptionFromJwtClaims'
import {
  buildPremiumCheckoutAnalyticsContext,
  buildPremiumCheckoutStartedProperties,
  buildPremiumSubscribedProperties,
  buildSignUpClickProperties,
  buildSignUpSuccessProperties,
  buildSignupAnalyticsContext,
  getSubscriptionAnalyticsTier,
  type SubscriptionAnalyticsTargetTier,
  type PremiumCheckoutAnalyticsContext,
  type SignupAnalyticsContext,
} from '@/utils/signupPremiumAnalytics'
import {
  captureAttributionFromSearch,
  getAttributionContext,
} from '@/utils/attributionContext'
import { hasPhotoshootHandoffOrigin } from '@/utils/photoshootHandoffAttribution'
import type { SubscriptionStore } from '@/services/subscriptionStore'
import { getPaddleCheckoutSummary, paddleCheckoutDiscountApplied, paddleCheckoutItemStartsTrial, type PaddleCheckoutSummary } from './paddleCheckoutSummary'
import { isPurchaseInEffect, isSubscriptionPurchase, purchaseStoreId, selectLimitedTimeOfferSubscription, type OfferSubscription, type PurchaseRecord } from '@/utils/activePurchase'

/** REST profile fields must not overwrite JWT-derived subscription tier flags (see docs/SUBSCRIPTION_TIER_WEB.md). */
const USER_PROFILE_FIELDS_EXCLUDED_FROM_TOKEN_TIER = [
  'userIsPremium',
  'userIsSuper',
  'subscription_level',
  'is_premium',
] as const

/** Paddle.js v2: payload is always on `event.data`, not the root event object. */
function paddleCheckoutEventData(event: { name?: string; data?: unknown }): Record<string, unknown> {
  const raw = event?.data
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>
  }
  return {}
}

/** Billing checkout events nest payment details under `data.payment`. */
function paddlePaymentMethodFromBillingData(data: Record<string, unknown>): string | undefined {
  const pay = data.payment
  if (pay && typeof pay === 'object' && !Array.isArray(pay)) {
    const p = pay as Record<string, unknown>
    const md = p.method_details
    if (md && typeof md === 'object' && !Array.isArray(md)) {
      const t = (md as Record<string, unknown>).type
      if (typeof t === 'string' && t.trim()) return t
    }
    for (const key of ['method', 'type', 'payment_method'] as const) {
      const v = p[key]
      if (typeof v === 'string' && v.trim()) return v
    }
  }
  const top = data.payment_method
  return typeof top === 'string' && top.trim() ? top : undefined
}

/**
 * Returns true when the given Paddle price ID is configured (via env) with a
 * non-zero trial period. Used to decide between GA `start_trial` and
 * `purchase` events when the Billing event payload doesn't explicitly tell us.
 */
function isTrialPaddlePriceId(priceId: string | null | undefined): boolean {
  if (!priceId) return false
  const monthly = process.env.REACT_APP_PADDLE_PRICE_MONTHLY
  const yearlyWith = process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITH_TRIAL
  const yearlyWithout = process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITHOUT_TRIAL
  if (priceId === monthly) {
    return parseInt(process.env.REACT_APP_PADDLE_TRIAL_DAYS_MONTHLY || '0', 10) > 0
  }
  if (priceId === yearlyWith) {
    return parseInt(process.env.REACT_APP_PADDLE_TRIAL_DAYS_YEARLY_WITH_TRIAL || '0', 10) > 0
  }
  if (priceId === yearlyWithout) {
    return parseInt(process.env.REACT_APP_PADDLE_TRIAL_DAYS_YEARLY_WITHOUT_TRIAL || '0', 10) > 0
  }
  return false
}

/**
 * Discounted monthly Super price offered right after a monthly Premium
 * checkout (ModalLimitedTimeOffer). It is a plan *change* price: it is never
 * passed to `Paddle.Checkout.open`, only to `updatePaddleSubscriptionPrice`.
 */
export function getSpecialOfferSuperMonthlyPriceId(): string {
  return (process.env.REACT_APP_PADDLE_PRICE_SPECIAL_OFFER_UPGRADE_SUPER_MONTHLY || '').trim()
}

/**
 * Paddle discount (`dsc_…`) for the limited offer shown to free users. Each
 * environment sets its own value (sandbox in dev/staging, live in production);
 * empty means the discount isn't configured yet.
 */
export function getLimitedOfferDiscountId(): string {
  return (process.env.REACT_APP_PADDLE_DISCOUNT_LIMITED_OFFER || '').trim()
}

/** The limited offer is the yearly Premium plan without a trial, with the discount above. */
export function getLimitedOfferPriceId(): string {
  return (process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITHOUT_TRIAL || '').trim()
}

export type LocalizedPrice = { display: string; amount: number; currencyCode?: string; countryCode?: string }

export type LimitedOfferPrices = {
  priceId: string
  regular: LocalizedPrice
  /** `null` when no discount is configured or Paddle doesn't lower the price. */
  discounted: LocalizedPrice | null
  /** The discount to pass to checkout; `null` whenever `discounted` is. */
  discountId: string | null
}

function isSuperPaddlePriceId(priceId: string | null | undefined): boolean {
  if (!priceId) return false
  return (
    priceId === process.env.REACT_APP_PADDLE_PRICE_BAZAART_SUPER_MONTHLY ||
    priceId === process.env.REACT_APP_PADDLE_PRICE_BAZAART_SUPER_YEARLY ||
    priceId === getSpecialOfferSuperMonthlyPriceId()
  )
}

function getPaddleAnalyticsTargetTier(priceId: string | null | undefined): SubscriptionAnalyticsTargetTier {
  if (!priceId) return 'Unknown'
  if (isSuperPaddlePriceId(priceId)) return 'Super'
  if ([process.env.REACT_APP_PADDLE_PRICE_MONTHLY,
    process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITH_TRIAL,
    process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITHOUT_TRIAL].includes(priceId)) return 'Premium'
  return 'Unknown'
}

function paddleTotalsFromBillingData(data: Record<string, unknown>): { total?: string; tax?: string } {
  const totals =
    data.totals && typeof data.totals === 'object' && !Array.isArray(data.totals)
      ? (data.totals as Record<string, unknown>)
      : {}
  const recurring =
    data.recurring_totals &&
    typeof data.recurring_totals === 'object' &&
    !Array.isArray(data.recurring_totals)
      ? (data.recurring_totals as Record<string, unknown>)
      : {}
  return {
    total: (recurring.total ?? totals.total ?? totals.grand_total) as string | undefined,
    tax: (recurring.tax ?? totals.tax) as string | undefined,
  }
}

function getAnalyticsErrorDetails(error: unknown): { errorCode: string; errorMessage: string } {
  if (!error) {
    return { errorCode: 'unknown', errorMessage: 'Unknown error' }
  }

  if (error instanceof Error) {
    const anyError = error as Error & { code?: string }
    return {
      errorCode: typeof anyError.code === 'string' ? anyError.code : 'error',
      errorMessage: error.message || 'Unknown error',
    }
  }

  if (typeof error === 'object') {
    const err = error as {
      code?: unknown
      message?: unknown
      response?: {
        data?: {
          code?: unknown
          message?: unknown
          error?: { code?: unknown; message?: unknown }
        }
      }
    }

    const errorCode =
      (typeof err.code === 'string' && err.code) ||
      (typeof err.response?.data?.error?.code === 'string' && err.response.data.error.code) ||
      (typeof err.response?.data?.code === 'string' && err.response.data.code) ||
      'error'

    const errorMessage =
      (typeof err.message === 'string' && err.message) ||
      (typeof err.response?.data?.error?.message === 'string' && err.response.data.error.message) ||
      (typeof err.response?.data?.message === 'string' && err.response.data.message) ||
      'Unknown error'

    return { errorCode, errorMessage }
  }

  if (typeof error === 'string') {
    return { errorCode: 'error', errorMessage: error }
  }

  return { errorCode: 'error', errorMessage: 'Unknown error' }
}

type Callback = (...args: any[]) => any

type GoogleButtonWrapper = {
  state: string
  renew: () => GoogleButtonWrapper
  click: () => void
  remove: () => void
}

type PaymentDependencyName = 'facebook' | 'google' | 'apple' | 'paddle'

type PaymentDependencyRegistry = {
  allDependencies: Record<PaymentDependencyName, string>
  loadedDependencies: Record<string, boolean>
  addListener: (dependency: string, listener: () => void) => void
}

type PlanInfo = {
  plan_id: string | undefined
  recurring: {
    price: { gross: string; amount: number }
    currencyCode?: string
    countryCode?: string
    subscription: { trial_days: number }
  }
}

export type AllPlanPrices = {
  monthly: PlanInfo
  yearly: PlanInfo
  monthlySuper: PlanInfo
  yearlySuper: PlanInfo
}

export interface PaddleCheckoutOptions {
  onError?: () => void
  onDiscountStatus?: (applied: boolean) => void
  onCheckoutUpdated?: (summary: PaddleCheckoutSummary | null) => void
  layout?: 'fixed' | 'fluid'
}

export enum PremiumOnPlatformType {
  android,
  ios,
  web,
}

export class SignInManager {
  platformType: string | null
  userId: number | null
  username: string | null
  isStaff: boolean | null
  email: string | null
  userIsPremium: boolean | null
  /** True only for `subscription_level: super`; super users always count as premium for gates. */
  userIsSuper: boolean | null
  userHaveActivePaddleSubscription: boolean | null
  userPremiumOnPlatform: PremiumOnPlatformType | null
  manageCancelUrl: string | null
  manageUpdateUrl: string | null
  /** Original transaction / subscription id for GET /purchase/txid/ (Paddle Billing). */
  paddleWebSubscriptionId: string | null = null
  userHadAFreeTrial: boolean | null
  dataForAnalytics: PremiumCheckoutAnalyticsContext | null
  signupAnalyticsContext: SignupAnalyticsContext | null
  private specialOfferUpgradeInFlight = false
  private subscriptionSelectionReady = false
  private transactionRefreshInFlight: { session: object; maxAttempts: number; promise: Promise<boolean> } | null = null
  private offerSession = {}
  private activeOfferPurchases: PurchaseRecord[] = []
  private offerSubscription: { userId: number; checkedAt: number; value: OfferSubscription | null } | null = null
  private offerRefreshInFlight: Promise<void> | null = null
  private offerStoreCache = new Map<number, Promise<SubscriptionStore>>()

  /** Optional upsell reads never block the action that triggered them. */
  public getLimitedTimeOfferSubscription(): OfferSubscription | null {
    const cached = this.offerSubscription
    if (!this.userId || !this.userIsPremium || this.userIsSuper) return null
    if (!cached || cached.userId !== this.userId || Date.now() - cached.checkedAt >= 5 * 60 * 1000) {
      void this.ensureLimitedTimeOfferSubscriptionFresh()
      return null
    }
    return cached.value && cached.value.subscriptionId === this.paddleWebSubscriptionId && cached.value.expiresAt > Date.now() ? cached.value : null
  }

  public ensureLimitedTimeOfferSubscriptionFresh(force = false): Promise<void> {
    if (!this.userId || !this.userIsPremium || this.userIsSuper) return Promise.resolve()
    if (this.offerRefreshInFlight) return this.offerRefreshInFlight
    if (!force && this.offerSubscription?.userId === this.userId &&
      Date.now() - this.offerSubscription.checkedAt < 5 * 60 * 1000) return Promise.resolve()
    const session = this.offerSession
    this.offerSubscription = null
    const pending = (async () => {
      try {
        await this.refreshTransactionStatus()
      } catch {
        // Unavailable eligibility does not change the requested download/save.
      }
    })().finally(() => {
      if (this.offerSession === session && this.offerRefreshInFlight === pending) this.offerRefreshInFlight = null
    })
    this.offerRefreshInFlight = pending
    return pending
  }

  private async loadPurchaseStoreMetadata(purchases: PurchaseRecord[]): Promise<Map<number, SubscriptionStore>> {
    if (purchases.some(p => isPurchaseInEffect(p) && purchaseStoreId(p) === null)) throw new Error('Active purchase has no valid Store id')
    const ids = Array.from(new Set(purchases.filter(p => isPurchaseInEffect(p)).map(purchaseStoreId).filter((id): id is number => id !== null)))
    const metadata = await Promise.all(ids.map(id => {
      let pending = this.offerStoreCache.get(id)
      if (!pending) {
        pending = ApiService.getSubscriptionStore(id).catch(error => {
          this.offerStoreCache.delete(id)
          throw error
        })
        this.offerStoreCache.set(id, pending)
      }
      return pending
    }))
    if (metadata.some(m => !['Subscriptions', 'Subscriptions Super', 'Additional Credits'].includes(m.categoryName ?? ''))) throw new Error('Unknown active purchase category')
    return new Map(metadata.map(m => [m.id, m]))
  }

  private async refreshLimitedTimeOfferSubscription(purchases: PurchaseRecord[], selectedId: string | null, stores?: Map<number, SubscriptionStore>): Promise<void> {
    const session = this.offerSession
    const userId = this.userId
    const input = purchases
    this.activeOfferPurchases = input
    this.offerSubscription = null
    if (!userId || !this.userIsPremium || this.userIsSuper) return
    try {
      const metadata = stores ?? await this.loadPurchaseStoreMetadata(input)
      if (session !== this.offerSession || userId !== this.userId || input !== this.activeOfferPurchases ||
        selectedId !== this.paddleWebSubscriptionId) return
      this.offerSubscription = {
        userId, checkedAt: Date.now(),
        value: selectLimitedTimeOfferSubscription(input, metadata, selectedId,
          (process.env.REACT_APP_PADDLE_PRICE_MONTHLY || '').trim()),
      }
    } catch {
      // Leave unknown; a later trigger can retry. Cache failures are not permanent.
    }
  }

  checkTransactionDone = false
  new_user: boolean | null

  cookies = new Cookies() // Keep for Google Analytics _ga cookie only
  didSetup = false
  didRefreshFirebaseToken = false
  
  didSetupPaddle = false
  private userDataLoadedPushedForId: number | null = null

  private callbacks: Record<string, Callback> = {};
  private paddle: any | null
  private appleId: any | null
  private google: any | null
  private FB: any | null

  private purchasePlanId: string | null
  private requestedDiscountCode: string | null = null
  private checkoutOptions: PaddleCheckoutOptions | null = null
  private checkoutCloseCallback: ((completed?: boolean) => void) | null = null
  private paddleCheckoutSucceeded = false
  private googleButtonWrapper: GoogleButtonWrapper | null
  private googleCloseCheckInterval: any | null
  private googleOriginalOpen: typeof window.open | null = null
  private googlePopup: Window | null = null
  private googleAuthInFlight = false
  private socialAttempt: AbortController | null = null
  private googleAttemptState: string | null = null

  private beginSocialLogin(): AbortController {
    this.cancelPendingSocialLogin()
    const attempt = new AbortController()
    this.socialAttempt = attempt
    return attempt
  }

  public cancelPendingSocialLogin(): void {
    this.socialAttempt?.abort()
    this.socialAttempt = null
    this.googleAttemptState = null
    const popup = this.googlePopup
    this.callbacks?.['google']?.(false)
    popup?.close()
  }

  private static instance: SignInManager

  private userInfo: UserInfo | null = null
  private aiModelsCreditsLastSyncedAt: number | null = null
  private aiModelsCreditsRefreshInFlight: Promise<boolean> | null = null
  private planPricesPromise: Promise<AllPlanPrices> | null = null
  private limitedOfferPricesPromise: Promise<LimitedOfferPrices> | null = null
  private limitedOfferPrices: LimitedOfferPrices | null = null
  private planPricesFetchedForHadTrial: boolean | null = undefined as unknown as null
  private constructor() {
    captureAttributionFromSearch(window.location.search)
    this.callbacks = {};
    this.dataForAnalytics = null
    this.signupAnalyticsContext = null
  }

  public static getInstance(): SignInManager {
    if (!SignInManager.instance) {
      SignInManager.instance = new SignInManager()
    }

    let userInfo = UserInfoStorage.get()
    if (userInfo) {
      SignInManager.instance.setUserProperties(userInfo)

      // Normal-reload bootstrap: refresh the Firebase token (idempotent via
      // didRefreshFirebaseToken) and prime location. Cross-account cleanup for
      // a silent re-auth is handled in completeSsoLogin (the SSO redeem path).
      SignInManager.instance.refreshFirebaseToken()
      LocationGeoIPService.shared.prefetchLocation()

      SignInManager.instance.pushUserDataLoadedToDataLayer()
    }

    // Prime Paddle price cache as early as possible so Metsikon and other
    // UI components can consume it without waiting on mount.
    void SignInManager.instance.warmUpPlanPrices()

    return SignInManager.instance
  }

  public setupUI(container: HTMLDivElement | null) {
    if (!container) return
    if (!this.didSetup) {
      this.didSetup = true
      this.setupLogic()
    }

    // Each provider must become usable as soon as its own SDK is ready.
    // Waiting for every social SDK (and historically Paddle) meant one blocked
    // third-party script disabled all sign-in methods on Safari/content blockers.
    void this.setupFacebook().catch((error) => {
      Sentry.captureException(error, { tags: { area: 'facebook-sign-in-setup' } })
    })
    void this.setupApple().catch((error) => {
      Sentry.captureException(error, { tags: { area: 'apple-sign-in-setup' } })
    })
    void this.setupGoogle().then(() => {
      this.googleButtonWrapper?.remove()
      this.googleButtonWrapper = this.createFakeGoogleWrapper(container)
    }).catch((error) => {
      Sentry.captureException(error, { tags: { area: 'google-sign-in-setup' } })
    })
  }

  public setSignupAnalyticsContext(context: SignupAnalyticsContext | null): void {
    this.signupAnalyticsContext = context
  }

  private getSignupAnalyticsContext(): SignupAnalyticsContext {
    return (
      this.signupAnalyticsContext ??
      buildSignupAnalyticsContext({
        source: null,
        type: 'Standard',
      })
    )
  }

  private getPremiumCheckoutAnalyticsContext(): PremiumCheckoutAnalyticsContext {
    return (
      this.dataForAnalytics ??
      buildPremiumCheckoutAnalyticsContext({
        source: null,
        type: 'Standard',
      })
    )
  }

  private emitSignupFailed(
    destination: string,
    error: unknown,
    authFlow: 'SignIn' | 'SignUp' | 'Unknown' = 'Unknown',
  ): void {
    const { errorCode, errorMessage } = getAnalyticsErrorDetails(error)
    customAmplitude(
      'Sign up error',
      {
        ...buildSignUpClickProperties({
          context: this.getSignupAnalyticsContext(),
          destination,
        }),
        error_code: errorCode,
        error_message: errorMessage,
        'Auth Flow': authFlow,
      },
      { necessary: true },
    )
  }

  private waitForDependency(dependencyName: PaymentDependencyName): Promise<void> {
    return new Promise((resolve) => {
      const paymentDependency = (
        window as typeof window & { paymentDependency: PaymentDependencyRegistry }
      ).paymentDependency
      const dependency = paymentDependency.allDependencies[dependencyName]
      if (paymentDependency.loadedDependencies[dependency]) {
        resolve();
      } else {
        paymentDependency.addListener(dependency, () => resolve());
      }
    });
  }

  private setupFacebook(): Promise<void> {
    return this.waitForDependency('facebook').then(() => {
      let that = this
      // @ts-ignore
      this.FB = window.FB
      if (this.FB) {
        this.FB.init({
          appId: process.env.REACT_APP_FB_APP_ID,
          cookie: true, // Enable cookies to allow the server to access the session.
          xfbml: true, // Parse social plugins on this webpage.
          version: 'v16.0', // Use this Graph API version for this call.
        })
      }
  
      // @ts-ignore
      window.fbAsyncInit = function () {
        // @ts-ignore
        that.FB = window.FB
        that.FB.init({
          appId: process.env.REACT_APP_FB_APP_ID,
          cookie: true, // Enable cookies to allow the serREACT_APP_FB_APP_IDver to access the session.
          xfbml: true, // Parse social plugins on this webpage.
          version: 'v16.0', // Use this Graph API version for this call.
        })
      }
    });
  }

  private setupGoogle(): Promise<void> {
    return this.waitForDependency('google').then(() => {
      let that = this
      // @ts-ignore
      this.google = window.google
      this.google?.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        ux_mode: 'popup',
        callback: async tokenResponse => {
          await that.handleCredentialResponse(tokenResponse)
        },
      })
    });
  }
  
  private setupApple(): Promise<void> {
    return this.waitForDependency('apple').then(() => {
      // @ts-ignore
      this.appleId = window.AppleID
    });
  }

  private setupLogic() {
    this.offerSession = {}
    this.subscriptionSelectionReady = false
    this.offerSubscription = null
    this.activeOfferPurchases = []
    this.offerRefreshInFlight = null
    this.callbacks = {}
    this.userIsPremium = false
    this.userIsSuper = false
    this.isStaff = false
    this.manageCancelUrl = null
    this.manageUpdateUrl = null
    this.paddleWebSubscriptionId = null
    this.userPremiumOnPlatform = null
    this.aiModelsCreditsLastSyncedAt = null
    this.aiModelsCreditsRefreshInFlight = null
  }

  public setupPaddle() {
    if (this.didSetupPaddle) {
      return
    }

    this.paddle = window.Paddle
    if (this.paddle == undefined) {
      return
    }

    const token = (process.env.REACT_APP_PADDLE_CLIENT_TOKEN || '').trim()
    if (!token) {
      console.warn('REACT_APP_PADDLE_CLIENT_TOKEN is not set; Paddle Billing checkout is disabled.')
      this.didSetupPaddle = true
      return
    }

    this.didSetupPaddle = true

    const that = this
    if (process.env.REACT_APP_IS_SANDBOX === 'true') {
      this.paddle.Environment.set('sandbox')
    }

    const setPaddleFrame = (showCheckout: boolean) => {
      const checkoutEl = document.getElementById('paddle-checkout')
      const loaderEl = document.getElementById('paddle-checkout-loader')
      if (!checkoutEl || !loaderEl) {
        return
      }
      if (showCheckout) {
        checkoutEl.style.setProperty('display', 'block', 'important')
        loaderEl.style.setProperty('display', 'none', 'important')
      } else {
        checkoutEl.style.setProperty('display', 'none', 'important')
        loaderEl.style.setProperty('display', 'block', 'important')
      }
    }

    const persistPostCheckoutUser = () => {
      // Delegate to the single shared persistence helper so user/storage/store
      // stay aligned (UserInfoStorage + Redux setUser + setUserIsPremium +
      // setUserIsSuper).
      that.persistUserStateToStoreAndStorage()

      const identifyEvent = new amplitude.Identify()
      identifyEvent.set('isPremium', !!that.userIsPremium)
      identifyEvent.set('isSuper', !!that.userIsSuper)
      amplitude.identify(identifyEvent)
    }

    try {
      this.paddle.Initialize({
        token,
        eventCallback: function (event: {
          name?: string
          data?: Record<string, unknown>
          code?: string
          detail?: string
        }) {
          const name = event?.name
          const data = paddleCheckoutEventData(event)
          const checkoutContext = that.getPremiumCheckoutAnalyticsContext()
          const tierProperties = {
            'Target Tier': checkoutContext['Target Tier'],
            'Previous Tier': checkoutContext['Previous Tier'],
          }

          const params = {
            ...tierProperties,
            isSandbox: process.env.REACT_APP_IS_SANDBOX,
            userId: that.userId,
            source: that.platformType,
            email: that.email,
          }

          if (name === 'checkout.loaded' || name === 'checkout.updated') {
            that.reportCheckoutDiscountStatus(data)
          }
          if (name === 'checkout.discount.applied' || name === 'checkout.discount.removed') {
            that.reportCheckoutDiscountStatus(data, name === 'checkout.discount.applied')
          }
          if (name === 'checkout.loaded' || name === 'checkout.updated' ||
              name === 'checkout.customer.created' || name === 'checkout.customer.updated' ||
              name === 'checkout.discount.applied' || name === 'checkout.discount.removed' ||
              name === 'checkout.items.updated') {
            that.checkoutOptions?.onCheckoutUpdated?.(getPaddleCheckoutSummary(data, that.purchasePlanId))
          }

          if (name === 'checkout.loaded') {
            setPaddleFrame(true)
            customAmplitude('Checkout.Loaded', params)
            return
          }

          if (name === 'checkout.payment.selected') {
            const pm = paddlePaymentMethodFromBillingData(data)
            customAmplitude('Checkout.PaymentMethodSelected', {
              ...tierProperties,
              'Payment Method': pm,
            })
            return
          }

          // Billing: fired when customer commits to pay (card flow); closer to "add_payment_info" than selection alone.
          if (name === 'checkout.payment.initiated') {
            const pm = paddlePaymentMethodFromBillingData(data)
            customAmplitude('Checkout.PaymentInitiated', {
              ...tierProperties,
              'Payment Method': pm,
              'Payment Type': pm ?? 'unknown',
              Plan: checkoutContext.Duration ?? 'Unknown',
            })
            void sha256Hex(String(that.email || '')).then((hash_email) => {
              const attribution = getAttributionContext()
              // @ts-ignore
              window.dataLayer.push({
                event: 'add_payment_info',
                customer_id: that.userId,
                user_email: hash_email,
                ...attribution,
              })
            })
            return
          }

          if (
            name === 'checkout.payment.failed' ||
            name === 'checkout.payment.error' ||
            name === 'checkout.error'
          ) {
            const code =
              (typeof event.code === 'string' && event.code) ||
              (typeof data.code === 'string' && data.code) ||
              (typeof data.error_code === 'string' && data.error_code) ||
              name ||
              'unknown'
            const msg =
              (typeof event.detail === 'string' && event.detail) ||
              (typeof data.detail === 'string' && data.detail) ||
              (typeof data.message === 'string' && data.message) ||
              JSON.stringify(data)
            const errorReason = JSON.stringify({ code, message: msg, data })
            const errorTransactionId =
              typeof data.transaction_id === 'string' ? (data.transaction_id as string) : undefined
            customAmplitude('Checkout.Error', { ...params, errorReason, all_params: errorReason })
            const premiumSubscribedErrorPayload = buildPremiumSubscribedProperties({
              context: checkoutContext,
              destination: 'PaddleCheckout',
              lifecycleStage: 'Error',
            })
            customAmplitude('Premium Subscribed Error', {
              ...premiumSubscribedErrorPayload,
              id: that.purchasePlanId,
              'Checkout Id': errorTransactionId,
              'Error Reason': errorReason,
              isSandbox: process.env.REACT_APP_IS_SANDBOX,
            })
            // Initialization errors leave no usable checkout. Payment declines
            // stay inside Paddle so the buyer can try another payment method.
            if (name === 'checkout.error') that.checkoutOptions?.onError?.()
            return
          }

          if (name === 'checkout.completed') {
            that.paddleCheckoutSucceeded = true
            setPaddleFrame(false)

            const paymentMethod = paddlePaymentMethodFromBillingData(data)
            const totals = paddleTotalsFromBillingData(data)
            const transactionId = (data.transaction_id as string | undefined) || undefined
            const currencyCode = (data.currency_code as string | undefined) || undefined
            const items = Array.isArray(data.items) ? data.items : []
            const first = items[0] as Record<string, unknown> | undefined
            const priceObj =
              first?.price && typeof first.price === 'object' && !Array.isArray(first.price)
                ? (first.price as Record<string, unknown>)
                : undefined
            const itemId =
              (typeof first?.price_id === 'string' && first.price_id) ||
              (typeof priceObj?.id === 'string' && priceObj.id) ||
              that.purchasePlanId
            const itemName =
              (typeof priceObj?.name === 'string' && priceObj.name) || 'subscription'
            // Detect whether this purchase started a trial from the event
            // payload (item-level trial_period, as Paddle.js sends it, or the
            // nested price). Fall back to the env mapping for the item's own
            // price ID only when the payload has no trial metadata.
            const initialTotal = (data.totals as Record<string, unknown> | undefined)?.total
            const trialStarted = !(Number(initialTotal) > 0) && paddleCheckoutItemStartsTrial(
              first, isTrialPaddlePriceId, that.purchasePlanId)

            measureChatGPTCheckout(transactionId,
              (data.totals as Record<string, unknown> | undefined)?.total, currencyCode,
              { trialStarted, planId: itemId })

            const premiumSubscribedPayload = buildPremiumSubscribedProperties({
              context: checkoutContext,
              destination: 'PaddleCheckout',
              lifecycleStage: trialStarted ? 'TrialStarted' : 'PurchaseCompleted',
            })

            customAmplitude(
              'Premium Subscribed',
              {
                ...premiumSubscribedPayload,
                id: that.purchasePlanId,
                'Target Tier': getPaddleAnalyticsTargetTier(itemId),
                Plan: checkoutContext.Duration ?? 'Unknown',
                Price: totals.total,
                Currency: currencyCode,
                'Checkout Id': transactionId,
                'Payment Method': paymentMethod,
                isSandbox: process.env.REACT_APP_IS_SANDBOX,
              },
              { necessary: true },
            )

            void sha256Hex(String(that.email || '')).then((hash_email) => {
              const attribution = getAttributionContext()
              if (trialStarted) {
                // @ts-ignore
                window.dataLayer.push({
                  event: 'start_trial',
                  customer_id: that.userId,
                  user_email: hash_email,
                  ...attribution,
                })
              } else {
                // @ts-ignore
                window.dataLayer.push({
                  event: 'purchase',
                  customer_id: that.userId,
                  user_email: hash_email,
                  ...attribution,
                  ecommerce: {
                    transaction_id: transactionId,
                    tax: totals.tax,
                    currency: currencyCode,
                    value: totals.total,
                    items: first
                      ? [
                          {
                            item_id: itemId,
                            quantity: 1,
                            item_name: itemName,
                            price: totals.total,
                            currency: currencyCode,
                          },
                        ]
                      : [],
                  },
                })
              }
            })

            setTimeout(async () => {
              // Backend/access-token subscription claims are canonical: wait
              // for the refreshed token to reflect the *purchased* tier. For
              // Premium→Super upgrades `userIsPremium` is already true before
              // checkout, so polling on premium alone would short-circuit and
              // persist with `userIsSuper=false` (blocks the AI Models HD
              // post-upgrade continuation in Metsikon).
              const needsSuper = isSuperPaddlePriceId(that.purchasePlanId)
              const tierOk = await that.waitForPremiumToken(15, needsSuper)
              that.checkTransactionDone = true

              if (!tierOk) {
                Sentry.captureMessage('Paddle Billing: checkout.completed but token did not reach required tier', {
                  level: 'warning',
                  extra: { transactionId, userId: that.userId, needsSuper },
                })
                persistPostCheckoutUser()
                return
              }

              // Optimistically flip web/active flags and clear any stale
              // manage URLs from a previous subscription. The real
              // `paddleWebSubscriptionId` + manage URLs are resolved below
              // via `refreshTransactionStatus` (listTransactions) — at this
              // point the backend has accepted the transaction, so it will
              // return it with the subscription id + manage/cancel URLs.
              // Fire and forget; these URLs are only needed later for the
              // Manage / Cancel buttons in MiniMenu.
              that.userPremiumOnPlatform = PremiumOnPlatformType.web
              that.userHaveActivePaddleSubscription = true
              that.manageUpdateUrl = null
              that.manageCancelUrl = null
              persistPostCheckoutUser()

              // Re-fetch the full user profile so server-side fields that aren't
              // carried on the JWT (notably `remaining_credits`,
              // `total_monthly_credits`, `next_credits_reset`) get refreshed for
              // the new subscription. Without this, a user who re-subscribes
              // after their previous plan was depleted/canceled keeps the stale
              // `remaining_credits: 0` in Redux/localStorage and immediately
              // hits the "AI limit reached" popup on re-entry to AI Models.
              // Sequenced before `refreshTransactionStatus` to avoid two
              // concurrent `persistUserStateToStoreAndStorage()` writes racing.
              // JWT tier flags are re-applied inside this call, so it cannot
              // clobber the just-confirmed premium/super tier.
              try {
                await that.refreshCurrentUserProfileFromServer()
              } catch (e) {
                console.error('Post-checkout refreshCurrentUserProfileFromServer failed:', e)
              }

              void that.refreshTransactionStatus(10).catch((e) =>
                console.error('Post-checkout refreshTransactionStatus failed:', e),
              )
            }, 0)
            return
          }

          if (name === 'checkout.closed') {
            const cb = that.checkoutCloseCallback
            that.checkoutCloseCallback = null
            that.checkoutOptions = null
            that.requestedDiscountCode = null
            cb?.(that.paddleCheckoutSucceeded)
            if (!that.paddleCheckoutSucceeded) {
              setPaddleFrame(false)
            }
            that.paddleCheckoutSucceeded = false
            return
          }

          // Map Billing event names to the existing Amplitude event names where
          // an old Paddle Classic dashboard depends on the PascalCase form
          // (only `checkout.failed` ↔ `Checkout.Failed` overlaps with Classic).
          // Billing-only events are passed through with their native names.
          const interestingMap: Record<string, string> = {
            'checkout.failed': 'Checkout.Failed',
            'checkout.items.updated': 'checkout.items.updated',
            'checkout.updated': 'checkout.updated',
            'checkout.warning': 'checkout.warning',
          }
          if (name && name in interestingMap) {
            customAmplitude(interestingMap[name], params)
          }
        },
      })
    } catch (e) {
      Sentry.captureException(e, { tags: { area: 'paddle-init' } })
      console.error('Paddle.Initialize failed', e)
      // Allow a subsequent setupPaddle() call to retry (e.g., if token was
      // misconfigured and is later corrected, or if the SDK was not ready).
      this.didSetupPaddle = false
    }
  }

  async logout(){
    this.cancelPendingSocialLogin()
    this.offerSession = {}
    this.subscriptionSelectionReady = false
    this.offerSubscription = null
    this.activeOfferPurchases = []
    this.offerRefreshInFlight = null
    api.clearAccessToken()
    AdminApiService.clearAccessToken()
    UserInfoStorage.remove()
    syncOneSignalUser(null)
    LocationGeoIPService.shared.clearCache()
    // Deliberately NOT clearing the photoshoot-handoff marker here: it's a
    // device/browser-level anti-gaming signal bounded by its own 1-hour TTL
    // (see photoshootHandoffAttribution.ts), not a per-session attribute.
    // Clearing it on logout would let a user bypass the yearly-trial
    // withholding simply by logging out and signing into a different (or
    // fresh) account on the same browser -- exactly the "clear session to
    // game the trial" path this gate exists to prevent.
    SignInManager.getInstance().setUserProperties({
      userId: null,
      username: null,
      platformType: null,
      email: null,
      userIsPremium: null,
      userIsSuper: null,
      userHaveActivePaddleSubscription: null,
      manageCancelUrl: null,
      manageUpdateUrl: null,
      paddleWebSubscriptionId: null,
      userPremiumOnPlatform: null,
      userHadAFreeTrial: null,
      accessToken: null,
      firebase_token: null
    })

    await SyncManager.shared.signOutFirebase();

    this.google?.accounts.id.disableAutoSelect();
    this.stopGoogleAttemptTracking()
    this.googleAuthInFlight = false
    this.callbacks = {};
    this.userDataLoadedPushedForId = null
  }

  /** Refiner/GTM: identify restored or active session by customer_id only. */
  private pushUserDataLoadedToDataLayer(): void {
    if (this.userId == null || this.userDataLoadedPushedForId === this.userId) return
    this.userDataLoadedPushedForId = this.userId
    const attribution = getAttributionContext()
    // @ts-ignore – GTM dataLayer is injected by index.html
    window.dataLayer = window.dataLayer || []
    // @ts-ignore
    window.dataLayer.push({ event: 'userDataLoaded', customer_id: this.userId, ...attribution })
  }

  // https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js/configuring_your_webpage_for_sign_in_with_apple
  async loginWithApple(): Promise<any> {
    if (!this.appleId?.auth?.signIn) {
      throw new Error('Apple Sign-In SDK is unavailable')
    }

    const attempt = this.beginSocialLogin()
    try {
      // Keep init and signIn in the same user-gesture turn. This matches the
      // working Photoshoot flow and avoids stale popup state on mobile WebKit.
      this.appleId.auth.init({
        clientId: 'login.co.bazaart.app',
        scope: 'name email',
        redirectURI: window.location.origin,
        state: 'origin:web',
        usePopup: true,
      })
      const event = await this.appleId.auth.signIn()
      
      if (attempt.signal.aborted) return null

      // User closed popup or auth failed
      if (!event || !event.authorization) {
        return null
      }
      
      const token = event.authorization.code
      if (!token) {
        throw new Error('Apple Sign-In did not return an authorization code')
      }
      // consecutive runs don't return the user's email
      const maybeEmail = event.user ? event.user.email : null

      let bazaartUser = await this.logIn(token, 'apple', null, attempt.signal)
      if (!bazaartUser || attempt.signal.aborted) return null
      this.email = bazaartUser.email ? bazaartUser.email : maybeEmail

      let user = this.getUserProperties()
      return user
    } catch (error) {
      if (attempt.signal.aborted) return null
      const code =
        error && typeof error === 'object'
          ? String((error as { error?: unknown; code?: unknown }).error ?? (error as { code?: unknown }).code ?? '')
          : ''
      if (code === 'popup_closed_by_user' || code === 'user_cancelled_authorize') {
        return null
      }
      throw error
    }
  }

  async loginWithFacebook(): Promise<any> {
    if (!this.FB?.login) {
      throw new Error('Facebook Sign-In SDK is unavailable')
    }

    const attempt = this.beginSocialLogin()
    let that = this
    return new Promise(function (resolve, reject) {
      that.FB.login(
        function (response) {
          if (attempt.signal.aborted || !response.authResponse) {
            resolve(null)
            return
          }
          if (response.status !== 'connected') {
            resolve(null)
            return
          }
          const accessToken = response.authResponse.accessToken
          const userId = response.authResponse.userID
          // @ts-ignore
          that.FB.api('/me', 'get', { fields: 'id,email' }, function (response) {
            setTimeout(function () {
              if (attempt.signal.aborted) {
                resolve(null)
                return
              }
              const maybeEmail = response.email
              that.logIn(accessToken, 'facebook', userId, attempt.signal).then((bazaartUser) => {
                if (!bazaartUser || attempt.signal.aborted) {
                  resolve(null)
                  return
                }
                that.email = bazaartUser.email ? bazaartUser.email : maybeEmail
                resolve(that.getUserProperties())
              }).catch(error => attempt.signal.aborted ? resolve(null) : reject(error))
            }, 0)
          })
        },
        { scope: 'email,public_profile' }
      )
    })
  }

  // https://stackoverflow.com/a/71394671/126015
  async loginWithGoogle(): Promise<any> {
    if (!this.googleButtonWrapper) {
      throw new Error('Google Sign-In SDK is unavailable')
    }

    const attempt = this.beginSocialLogin()
    const wrapper = this.googleButtonWrapper
    this.googleAttemptState = wrapper.state
    return new Promise((resolve, reject) => {
      this.stopGoogleAttemptTracking()
      if(!this.callbacks){
        this.emitSignupFailed('Google', new Error('callbacks in loginWithGoogle is undefined'), 'SignIn')
        Sentry.captureException(new Error('callbacks in loginWithGoogle is undefined'), { level: 'info' })
      }
      this.callbacks['google'] = (success: boolean, error?: Error) => {
        wrapper.remove()
        this.stopGoogleAttemptTracking()
        this.googleAuthInFlight = false
        this.googleAttemptState = null
        delete this.callbacks['google']

        if (error) {
          reject(error)
          return
        }
        if (success && !attempt.signal.aborted) {
          let user = this.getUserProperties()
          resolve(user)
        } else {
          resolve(null)
        }
      }
      this.attachGoogleReturnWatchers()

      try {
        wrapper.click()
      } catch (error) {
        const googleError = error instanceof Error ? error : new Error(String(error))
        this.callbacks['google']?.(false, googleError)
      } finally {
        this.googleButtonWrapper = wrapper.renew()
      }
    })
  }

  private async handleCredentialResponse(response) {
    // Each rendered button is used once. Google returns its state even when
    // an older popup answers after another provider or retry has started.
    if (!this.googleAttemptState || response?.state !== this.googleAttemptState || this.googleAuthInFlight) return
    const googleCallback = this.callbacks['google']
    const attempt = this.socialAttempt
    if (!googleCallback || !attempt || attempt.signal.aborted) return

    this.googleAuthInFlight = true
    this.stopGoogleAttemptTracking()

    try {
      if (!response?.credential) {
        googleCallback(false)
        return
      }

      // decodeJwtResponse() is a custom function defined by you
      // to decode the credential response.
      const payload = this.decodeJwtResponse(response.credential)
      const accessToken = response.credential
      const maybeEmail = payload.email

      const bazaartUser = await this.logIn(accessToken, 'google', payload.sub, attempt.signal)
      if (!bazaartUser || attempt.signal.aborted) return
      this.email = bazaartUser.email ? bazaartUser.email : maybeEmail
      googleCallback(true)
    } catch (error) {
      if (attempt.signal.aborted) return
      const googleError = error instanceof Error ? error : new Error(String(error))
      googleCallback(false, googleError)
    }
  }

  public getUserProperties() {
    return {
      ...this.userInfo,
      platformType: this.platformType,
      userId: this.userId,
      username: this.username,
      email: this.email,
      userIsPremium: this.userIsPremium,
      userIsSuper: this.userIsSuper,
      isStaff: this.isStaff,
      userHaveActivePaddleSubscription: this.userHaveActivePaddleSubscription,
      userPremiumOnPlatform: this.userPremiumOnPlatform,
      manageCancelUrl: this.manageCancelUrl,
      manageUpdateUrl: this.manageUpdateUrl,
      paddleWebSubscriptionId: this.paddleWebSubscriptionId,
      userHadAFreeTrial: this.userHadAFreeTrial,
      new_user: this.new_user
    }
  }
  
  async setUserProperties(userInfo) {
    const properties = [
      'userId',
      'username',
      'platformType',
      'email',
      'isStaff',
      'userIsPremium',
      'userIsSuper',
      'userHaveActivePaddleSubscription',
      'manageCancelUrl',
      'manageUpdateUrl',
      'paddleWebSubscriptionId',
      'userPremiumOnPlatform',
      'userHadAFreeTrial',
      'firebase_token'
    ];
    properties.forEach(property => {
      this[property] = userInfo[property];
    })
    this.userInfo = userInfo
  }

  /**
   * Resolves a localized price for the given Paddle price via
   * `Paddle.PricePreview` (Billing). Returns both the display string (for UI
   * labels) and the numeric amount + currency/country metadata (for any
   * derived math the UI needs, e.g. "monthly × 12" strike-through). Falls
   * back to the supplied env display string (`$11.99` etc.) — with a
   * US-style parsed amount — whenever the Paddle SDK isn't ready, the API
   * fails, or the response shape is missing fields, so the UI never blanks
   * out.
   *
   * Note: `amount` divides Paddle's lowest-denomination integer (`totals.total`)
   * by 100. This is correct for the 2-decimal currencies we sell in today
   * (USD/EUR/GBP/...). It is latently wrong for zero-decimal currencies
   * (JPY, KRW) and 3-decimal currencies (BHD, KWD, OMR). Revisit if/when
   * those locales ship.
   */
  private async getLocalizedPrice(
    priceId: string,
    envFallback: string,
    discountId?: string,
  ): Promise<{ display: string; amount: number; currencyCode?: string; countryCode?: string }> {
    const fallback = {
      display: envFallback,
      amount: parseFloat((envFallback || '').replace(/[^0-9.]/g, '')) || 0,
    }
    if (!priceId) return fallback
    const paddle = this.paddle as
      | { PricePreview?: (cfg: Record<string, unknown>) => Promise<unknown> }
      | null
    if (!paddle || typeof paddle.PricePreview !== 'function') {
      return fallback
    }
    const address = LocationGeoIPService.shared.cachedLocation
      ? { countryCode: LocationGeoIPService.shared.cachedLocation.countryCode }
      : undefined

    type PreviewShape = {
      data?: {
        currencyCode?: string
        details?: {
          address?: { countryCode?: string }
          lineItems?: Array<{
            formattedTotals?: { subtotal?: string; total?: string }
            totals?: { subtotal?: string; total?: string }
          }>
        }
      }
    }
    type ResolvedPrice = { display: string; amount: number; currencyCode?: string; countryCode?: string }

    const parsePreview = (preview: PreviewShape | undefined): ResolvedPrice | null => {
      const lineItem = preview?.data?.details?.lineItems?.[0]
      // Pair display + numeric from the *same* field. `total` and `subtotal`
      // are different numbers (subtotal excludes tax/discount), and pairing
      // them avoids a currency/value mismatch if Paddle returns one half
      // (e.g. localized `9,99 €` headline) but not the other (forcing the
      // strike-through to derive from the US env fallback).
      //
      // Zero-decimal currencies (VND, JPY, KRW, …) have no subunit, so
      // Paddle's `totals.total` is already the full amount — dividing by 100
      // would produce prices 100× too small for derived math (per-month, etc.).
      const previewCurrencyCode = preview?.data?.currencyCode ?? ''
      const ZERO_DECIMAL_CURRENCIES = new Set([
        'BIF','CLP','DJF','GNF','JPY','KMF','KRW','MGA','PYG','RWF','UGX','VND','VUV','XAF','XOF','XPF',
      ])
      const amountDivisor = ZERO_DECIMAL_CURRENCIES.has(previewCurrencyCode.toUpperCase()) ? 1 : 100
      const pickPair = (
        disp: string | undefined,
        minor: string | undefined,
      ): { display: string; amount: number } | null => {
        const dispOk = typeof disp === 'string' && disp.trim()
        const minorInt = typeof minor === 'string' ? parseInt(minor, 10) : NaN
        if (!dispOk || !Number.isFinite(minorInt)) return null
        return { display: disp, amount: minorInt / amountDivisor }
      }
      const paddlePrice =
        pickPair(lineItem?.formattedTotals?.total, lineItem?.totals?.total) ??
        pickPair(lineItem?.formattedTotals?.subtotal, lineItem?.totals?.subtotal)
      if (!paddlePrice) return null
      return {
        display: paddlePrice.display,
        amount: paddlePrice.amount,
        currencyCode: preview?.data?.currencyCode,
        countryCode: preview?.data?.details?.address?.countryCode,
      }
    }

    // No paid monthly/yearly plan costs under 1 unit in any currency we sell
    // in (even the strongest — KWD/BHD/OMR — put the monthly well above 1).
    // A sub-1 amount is the fingerprint of a bad interim Paddle preview (the
    // reported ₪0.99/₪0.08 Safari glitch), so treat it as implausible and
    // don't cache it for the session.
    const isPlausible = (price: ResolvedPrice | null): price is ResolvedPrice =>
      !!price && Number.isFinite(price.amount) && price.amount >= 1

    const requestPreview = async (): Promise<PreviewShape | undefined> =>
      (await paddle.PricePreview({
        items: [{ priceId, quantity: 1 }],
        ...(address ? { address } : {}),
        ...(discountId ? { discountId } : {}),
      })) as PreviewShape | undefined

    let lastPreview: PreviewShape | undefined
    // One retry: the first PricePreview after Paddle.js becomes ready can still
    // occasionally return an interim/invalid preview. Re-fetch once before
    // giving up to the env fallback.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, 400))
        }
        lastPreview = await requestPreview()
        const resolved = parsePreview(lastPreview)
        if (isPlausible(resolved)) {
          return resolved
        }
      } catch (e) {
        console.warn('Paddle.PricePreview failed; using env fallback price', e)
        return fallback
      }
    }

    // Both attempts produced an implausible/unparseable price. Capture the raw
    // payload so a recurrence is diagnosable instead of guessed at, then fall
    // back to the env display price.
    Sentry.captureMessage('Paddle.PricePreview returned implausible price; using env fallback', {
      level: 'warning',
      tags: { area: 'paddle-price-preview' },
      extra: { priceId, envFallback, preview: lastPreview },
    })
    return fallback
  }

  /**
   * Localized price of `priceId` with a Paddle discount applied, or `null` when
   * the discount isn't configured or Paddle can't preview it. There is no env
   * fallback: an unknown discounted price must not be shown as a real one.
   */
  async getDiscountedPrice(
    priceId: string,
    discountId: string,
  ): Promise<{ display: string; amount: number; currencyCode?: string; countryCode?: string } | null> {
    if (!priceId || !discountId) return null
    const price = await this.getLocalizedPrice(priceId, '', discountId)
    return price.display ? price : null
  }

  /**
   * Localized regular and discounted yearly price for the limited offer,
   * fetched once per session. The regular price falls back to the env display
   * price; the discounted one has no fallback.
   */
  getLimitedOfferPrices(): Promise<LimitedOfferPrices> {
    if (!this.limitedOfferPricesPromise) {
      this.limitedOfferPricesPromise = (async () => {
        await this.waitForPaddle()
        this.setupPaddle()
        await this.waitForPaddleInitialized()
        const priceId = getLimitedOfferPriceId()
        const regular = await this.getLocalizedPrice(priceId, process.env.REACT_APP_PADDLE_DISPLAY_PRICE_YEARLY || '')
        const discountId = getLimitedOfferDiscountId()
        const preview = discountId ? await this.getDiscountedPrice(priceId, discountId) : null
        const discounted = preview && preview.amount < regular.amount ? preview : null
        const prices = { priceId, regular, discounted, discountId: discounted ? discountId : null }
        this.limitedOfferPrices = prices
        return prices
      })()
    }
    return this.limitedOfferPricesPromise
  }

  /** The limited-offer prices if they have already been fetched this session. */
  peekLimitedOfferPrices(): LimitedOfferPrices | null {
    return this.limitedOfferPrices
  }

  async getMonthlyPlan() {
    const plan_id = process.env.REACT_APP_PADDLE_PRICE_MONTHLY || ''
    const trialDays = parseInt(process.env.REACT_APP_PADDLE_TRIAL_DAYS_MONTHLY || '0', 10)
    const envDisplay = process.env.REACT_APP_PADDLE_DISPLAY_PRICE_MONTHLY || ''
    const price = await this.getLocalizedPrice(plan_id, envDisplay)
    return {
      plan_id,
      recurring: {
        price: { gross: price.display, amount: price.amount },
        currencyCode: price.currencyCode,
        countryCode: price.countryCode,
        subscription: { trial_days: trialDays },
      },
    }
  }

  /**
   * Yearly trial is withheld both for users who already had a trial
   * (backend `can_trial === false`) and for sessions that arrived via the
   * top-level photoshoot handoff (`markPhotoshootHandoffOrigin`), regardless
   * of their backend trial history. Reuses the exact same "no trial" price
   * ID / trial-days branch as the existing eligibility check.
   */
  private shouldWithholdYearlyTrial(): boolean {
    return Boolean(this.userHadAFreeTrial) || hasPhotoshootHandoffOrigin()
  }

  async getYearlyPlan() {
    const hadTrial = this.shouldWithholdYearlyTrial()
    const plan_id = hadTrial
      ? process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITHOUT_TRIAL || ''
      : process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITH_TRIAL || ''
    const trialDays = hadTrial
      ? parseInt(process.env.REACT_APP_PADDLE_TRIAL_DAYS_YEARLY_WITHOUT_TRIAL || '0', 10)
      : parseInt(process.env.REACT_APP_PADDLE_TRIAL_DAYS_YEARLY_WITH_TRIAL || '7', 10)
    const envDisplay = process.env.REACT_APP_PADDLE_DISPLAY_PRICE_YEARLY || ''
    const price = await this.getLocalizedPrice(plan_id, envDisplay)
    return {
      plan_id,
      recurring: {
        price: { gross: price.display, amount: price.amount },
        currencyCode: price.currencyCode,
        countryCode: price.countryCode,
        subscription: { trial_days: trialDays },
      },
    }
  }

  async getYearlySuperPlan() {
    const plan_id = process.env.REACT_APP_PADDLE_PRICE_BAZAART_SUPER_YEARLY
    const envDisplay = process.env.REACT_APP_PADDLE_DISPLAY_PRICE_SUPER_YEARLY || ''
    const price = await this.getLocalizedPrice(plan_id, envDisplay)
    return {
      plan_id,
      recurring: {
        price: { gross: price.display, amount: price.amount },
        currencyCode: price.currencyCode,
        countryCode: price.countryCode,
        subscription: { trial_days: 0 },
      },
    }
  }

  async getMonthlySuperPlan() {
    const plan_id = process.env.REACT_APP_PADDLE_PRICE_BAZAART_SUPER_MONTHLY || ''
    const trialDays = 0
    const envDisplay = process.env.REACT_APP_PADDLE_DISPLAY_PRICE_SUPER_MONTHLY || ''
    const price = await this.getLocalizedPrice(plan_id, envDisplay)
    return {
      plan_id,
      recurring: {
        price: { gross: price.display, amount: price.amount },
        currencyCode: price.currencyCode,
        countryCode: price.countryCode,
        subscription: { trial_days: trialDays },
      },
    }
  }

  private waitForPaddle(): Promise<void> {
    return new Promise((resolve) => {
      // @ts-ignore
      if (window.Paddle) { resolve(); return }
      const check = setInterval(() => {
        // @ts-ignore
        if (window.Paddle) { clearInterval(check); resolve() }
      }, 100)
      setTimeout(() => { clearInterval(check); resolve() }, 5000)
    })
  }

  /**
   * `Paddle.Initialize()` completes asynchronously — `window.Paddle` can exist
   * (script loaded) while the SDK is still resolving locale/currency/catalog.
   * Calling `Paddle.PricePreview()` in that window can return an interim/invalid
   * preview (e.g. a corrupted headline like `.99` and a tiny amount ~0.99),
   * which then gets cached in `planPricesPromise` for the whole tab session.
   * Paddle exposes an `Initialized` boolean flag for exactly this; poll it with
   * a bounded timeout so the UI never hangs if Paddle never reports ready.
   */
  private waitForPaddleInitialized(): Promise<void> {
    return new Promise((resolve) => {
      if (this.paddle?.Initialized === true) { resolve(); return }
      const check = setInterval(() => {
        if (this.paddle?.Initialized === true) { clearInterval(check); resolve() }
      }, 100)
      setTimeout(() => { clearInterval(check); resolve() }, 5000)
    })
  }

  async warmUpPlanPrices() {
    try {
      await this.getPlanPrices()
    } catch { /* non-critical -- priming Paddle SDK cache */ }
  }

  /**
   * Returns a single-flight promise that resolves to all four localized plan
   * prices (monthly, yearly, monthlySuper, yearlySuper). The result is cached
   * so callers (e.g. Metsikon) can call this without triggering redundant
   * Paddle.PricePreview network requests. The cache is invalidated automatically
   * when `shouldWithholdYearlyTrial()` changes (backend `userHadAFreeTrial` or
   * photoshoot-handoff origin), because the yearly plan ID differs based on
   * that condition.
   */
  public getPlanPrices(options?: { refresh?: boolean }): Promise<AllPlanPrices> {
    const currentHadTrial = this.shouldWithholdYearlyTrial()
    if (options?.refresh || (this.planPricesPromise && this.planPricesFetchedForHadTrial !== currentHadTrial)) {
      this.planPricesPromise = null
    }
    if (!this.planPricesPromise) {
      this.planPricesFetchedForHadTrial = currentHadTrial
      this.planPricesPromise = (async () => {
        await this.waitForPaddle()
        this.setupPaddle()
        // Wait until Paddle.js reports it is fully initialized before calling
        // PricePreview; otherwise the SDK can return a bad interim price that
        // gets cached for the session (Safari-prone race, fixed by refresh).
        await this.waitForPaddleInitialized()
        const [monthly, yearly, monthlySuper, yearlySuper] = await Promise.all([
          this.getMonthlyPlan(),
          this.getYearlyPlan(),
          this.getMonthlySuperPlan(),
          this.getYearlySuperPlan(),
        ])
        return { monthly, yearly, monthlySuper, yearlySuper }
      })()
    }
    return this.planPricesPromise
  }

  /** Single-shot per checkout: cleared once the outcome has been reported. */
  private reportCheckoutDiscountStatus(data: Record<string, unknown>, verdict?: boolean): void {
    const requestedCode = this.requestedDiscountCode
    if (!requestedCode) {
      return
    }
    const applied = verdict ?? paddleCheckoutDiscountApplied(data)
    if (applied === undefined) return
    this.requestedDiscountCode = null

    this.checkoutOptions?.onDiscountStatus?.(applied)

    customAmplitude('Checkout.DiscountStatus', {
      Status: applied ? 'Applied' : 'Rejected',
      'Coupon Code': requestedCode,
    })
  }

  pay(
    platformType,
    email,
    userId,
    didClose,
    purchasePlanId,
    analytics?: Partial<PremiumCheckoutAnalyticsContext>,
    /** Buyer-typed partner coupon. Paddle applies it as `discountCode`. */
    discountCode?: string,
    /** Paddle discount id for a new checkout (the limited offer). Not a coupon code. */
    discountId?: string,
    options?: PaddleCheckoutOptions,
  ) {
    if (!userId) {
      return
    }

    this.dataForAnalytics = buildPremiumCheckoutAnalyticsContext({
      source: analytics?.Source,
      type: analytics?.Type,
      duration: analytics?.Duration,
      email: analytics?.Email,
      page: analytics?.Page,
      pathname: window.location.pathname,
      targetTier: getPaddleAnalyticsTargetTier(purchasePlanId),
      previousTier: getSubscriptionAnalyticsTier(this),
    })

    // Single-subscription guarantee: a user who already has an active Paddle
    // subscription must never open a second checkout (that would create a
    // parallel subscription). Instead, switch their existing subscription's
    // price in place. Mirrors the iOS `hasActiveWebSubscription` → updatePaddlePlan
    // branch in SubscriptionManager.paddleDoSubscribe.
    if (this.userIsPremium && !this.userHaveActivePaddleSubscription && !this.subscriptionSelectionReady) {
      Sentry.captureMessage('Paddle checkout aborted: subscription classification is unresolved', { level: 'warning' })
      didClose?.(false)
      return
    }
    if (this.userHaveActivePaddleSubscription && purchasePlanId) {
      void this.updatePaddlePlan(String(purchasePlanId), didClose)
      return
    }

    if (!this.paddle || !(process.env.REACT_APP_PADDLE_CLIENT_TOKEN || '').trim()) {
      console.warn('Paddle Billing checkout is not configured.')
      options?.onError?.()
      return
    }
    this.paddleCheckoutSucceeded = false
    this.checkoutCloseCallback = didClose ?? null
    this.checkoutOptions = options ?? null

    const gaCookie = this.cookies.get('_ga')
    let gaClientId = null
    if (gaCookie) {
      gaClientId = gaCookie.split('.').slice(-2).join('.')
    }
    const attribution = getAttributionContext()
    const resolvedDiscountCode = (discountCode ?? '').trim()
    this.requestedDiscountCode = resolvedDiscountCode || null

    const internalPadding = 10
    // Paddle Billing requires min-width on inline frames so the MoR footer stays visible.
    // Phones narrower than the desktop column shrink the frame down to that minimum.
    const frameWidth = window.matchMedia?.(COMPACT_MODAL_QUERY).matches ? 'min(364px, 100%)' : '364px'
    const frameStyle = options?.layout === 'fluid'
      ? 'width: 100%; max-width: 384px; min-width: 312px; margin: 0; background-color: transparent; border: none'
      : `width: calc(${frameWidth} + ${
      internalPadding * 2
    }px); min-width: 312px; margin: 0 -${internalPadding}px; background-color: transparent; border: none`
    this.purchasePlanId = purchasePlanId

    customAmplitude(
      'Checkout.Started',
      buildPremiumCheckoutStartedProperties(this.getPremiumCheckoutAnalyticsContext()),
      { necessary: true },
    )

    const customer: {
      email: string
      address?: { countryCode?: string; postalCode?: string }
    } = {
      email: email || '',
    }
    if (LocationGeoIPService.shared.cachedLocation) {
      customer.address = {
        countryCode: LocationGeoIPService.shared.cachedLocation.countryCode,
        postalCode: LocationGeoIPService.shared.cachedLocation.postalCode,
      }
    }

    try {
      this.paddle.Checkout.open({
      settings: {
        displayMode: 'inline',
        variant: 'multi-page',
        frameTarget: 'paddle-checkout',
        frameInitialHeight: '450',
        frameStyle,
        allowLogout: false,
        showAddTaxId: false,
        showAddDiscounts: false,
      },
      items: [{ priceId: String(this.purchasePlanId), quantity: 1 }],
      ...(discountId ? { discountId } : {}),
      customer,
      ...(resolvedDiscountCode ? { discountCode: resolvedDiscountCode } : {}),
      customData: {
        source: 'webapp',
        ga_client_id: gaClientId,
        user_id: String(userId),
        bazaart_user_id: String(userId),
        coupon_code: resolvedDiscountCode || undefined,
        gclid: attribution.gclid || undefined,
        fbclid: attribution.fbclid || undefined,
        utm_source: attribution.utm_source || undefined,
        utm_medium: attribution.utm_medium || undefined,
        utm_campaign: attribution.utm_campaign || undefined,
        utm_content: attribution.utm_content || undefined,
        utm_term: attribution.utm_term || undefined,
      },
      })
    } catch (error) {
      if (!options?.onError) throw error
      Sentry.captureException(error, { tags: { area: 'paddle-checkout' } })
      options.onError()
    }
  }

  /**
   * Switches an existing Paddle subscriber's plan in place instead of opening a
   * second checkout. Mirrors iOS `SubscriptionManager.updatePaddlePlan`:
   * PATCH the subscription to the target price, then refresh the canonical JWT
   * (a change can move the user between tiers, e.g. premium → super) plus the
   * transaction-derived manage URLs before reporting success. `didClose(success)`
   * lets the caller (e.g. the Metsikon plan sheet) dismiss the same way the
   * Paddle `checkout.closed` callback does.
   *
   * Never falls back to opening a new checkout: if the subscription id cannot be
   * resolved the call fails rather than risk a parallel subscription.
   */
  /**
   * Post-purchase Premium → Super upsell (ModalLimitedTimeOffer). The user
   * already has an active Paddle subscription, so this always switches the
   * price in place via `updatePaddleSubscriptionPrice` — it never opens a
   * checkout, and it fails loudly (`didClose(false)`) if the special-offer
   * price id is not configured.
   */
  public async upgradeToSpecialOfferSuperMonthly(
    analytics: Partial<PremiumCheckoutAnalyticsContext> | undefined,
    didClose?: (completed?: boolean) => void,
    onPlanApplied?: () => void,
    expectedSubscriptionId?: string,
  ): Promise<void> {
    if (this.specialOfferUpgradeInFlight) { didClose?.(false); return }
    this.specialOfferUpgradeInFlight = true
    const previousTier = getSubscriptionAnalyticsTier(this)
    const session = this.offerSession
    const userId = this.userId
    const selectedId = expectedSubscriptionId ?? this.paddleWebSubscriptionId
    const stillOwned = () => session === this.offerSession && userId === this.userId &&
      selectedId === this.paddleWebSubscriptionId && String(store.getState().editor.user.user?.userId ?? store.getState().editor.user.user?.id) === String(userId)
    try {
      if (!stillOwned()) { didClose?.(false); return }
      await this.ensureLimitedTimeOfferSubscriptionFresh(true)
      const subscription = this.getLimitedTimeOfferSubscription()
      if (!stillOwned() || !subscription || subscription.subscriptionId !== selectedId) {
        didClose?.(false)
        return
      }
      const priceId = getSpecialOfferSuperMonthlyPriceId()
      if (!priceId) {
        console.error('upgradeToSpecialOfferSuperMonthly: REACT_APP_PADDLE_PRICE_SPECIAL_OFFER_UPGRADE_SUPER_MONTHLY is not set')
        Sentry.captureMessage('Limited-time Super offer aborted: missing special offer price id', {
          level: 'warning',
        })
        didClose?.(false)
        return
      }

      this.dataForAnalytics = buildPremiumCheckoutAnalyticsContext({
        source: analytics?.Source,
        type: analytics?.Type ?? 'Super',
        duration: analytics?.Duration ?? 'Monthly',
        email: analytics?.Email ?? this.email,
        page: analytics?.Page,
        pathname: window.location.pathname,
        targetTier: getPaddleAnalyticsTargetTier(priceId),
        previousTier,
      })

      await this.updatePaddlePlan(priceId, didClose, stillOwned, onPlanApplied)
    } finally { this.specialOfferUpgradeInFlight = false }
  }

  private async updatePaddlePlan(
    targetPriceId: string,
    didClose?: (completed?: boolean) => void,
    stillOwned: () => boolean = () => true,
    onPlanApplied?: () => void,
  ): Promise<void> {
    const priceId = String(targetPriceId || '').trim()

    let subscriptionId = String(this.paddleWebSubscriptionId || '').trim()
    if (!subscriptionId) {
      // The id may not be resolved yet (e.g. right after sign-in). Probe the
      // backend before giving up — but never open a second checkout.
      try {
        await this.refreshTransactionStatus(5)
      } catch (e) {
        console.error('updatePaddlePlan: failed to resolve subscription id', e)
      }
      subscriptionId = String(this.paddleWebSubscriptionId || '').trim()
    }

    if (!stillOwned()) { didClose?.(false); return }
    if (!subscriptionId || !priceId) {
      console.error(
        'updatePaddlePlan: missing subscription id or target price id; aborting to avoid a parallel subscription',
      )
      Sentry.captureMessage('updatePaddlePlan aborted: unresolved subscription id or price id', {
        level: 'warning',
        extra: { hasSubscriptionId: Boolean(subscriptionId), hasPriceId: Boolean(priceId) },
      })
      didClose?.(false)
      return
    }

    try {
      await ApiService.updatePaddleSubscriptionPrice(subscriptionId, priceId)
      onPlanApplied?.()
      if (!stillOwned()) { didClose?.(false); return }
      const needsSuper = isSuperPaddlePriceId(priceId)
      const tierOk = await this.waitForPremiumToken(15, needsSuper)
      if (!stillOwned()) { didClose?.(false); return }
      if (!tierOk) {
        Sentry.captureMessage('Paddle plan change: token did not reach required tier', {
          level: 'warning',
          extra: { subscriptionId, userId: this.userId, needsSuper },
        })
        this.persistUserStateToStoreAndStorage()
        didClose?.(false)
        return
      }

      // Persist immediately once the tier is confirmed (matching checkout flow).
      // Subscription tier flags (userIsPremium / userIsSuper) are canonical; the
      // profile/transaction fetches below enrich with credits and manage URLs but
      // must not block the upgrade completion if they fail.
      this.persistUserStateToStoreAndStorage()

      // Keep credit/profile fields in sync with the upgraded plan before the
      // caller resumes Super/credit-gated flows. Wrapped in try-catch so a failure
      // doesn't prevent didClose(true) after the tier was confirmed.
      try {
        await this.refreshCurrentUserProfileFromServer()
      } catch (e) {
        console.error('Post-plan-change refreshCurrentUserProfileFromServer failed:', e)
      }

      // Fire and forget: manage URLs are only needed later for Manage/Cancel buttons.
      void this.refreshTransactionStatus(10).catch((e) =>
        console.error('Post-plan-change refreshTransactionStatus failed:', e),
      )

      if (!stillOwned()) { didClose?.(false); return }
      const premiumSubscribedPayload = buildPremiumSubscribedProperties({
        context: this.getPremiumCheckoutAnalyticsContext(),
        destination: 'PaddlePlanChange',
        lifecycleStage: 'PlanChanged',
      })

      customAmplitude(
        'Premium Subscribed',
        {
          ...premiumSubscribedPayload,
          id: priceId,
          Plan:
            this.getPremiumCheckoutAnalyticsContext().Duration ??
            (priceId === process.env.REACT_APP_PADDLE_PRICE_MONTHLY ? 'Monthly' : 'Yearly'),
          'Payment Method': 'paddle_plan_change',
          isSandbox: process.env.REACT_APP_IS_SANDBOX,
        },
        { necessary: true },
      )

      didClose?.(true)
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'paddle-plan-change' } })
      const premiumSubscribedErrorPayload = buildPremiumSubscribedProperties({
        context: this.getPremiumCheckoutAnalyticsContext(),
        destination: 'PaddlePlanChange',
        lifecycleStage: 'Error',
      })
      customAmplitude('Premium Subscribed Error', {
        ...premiumSubscribedErrorPayload,
        id: priceId,
        'Error Reason': error instanceof Error ? error.message : String(error),
        isSandbox: process.env.REACT_APP_IS_SANDBOX,
      })
      console.error('updatePaddlePlan failed:', error)
      didClose?.(false)
    }
  }

  private async logIn(sessionToken, type, userId, signal: AbortSignal) {
    if (signal.aborted) return null
    const result = await ApiService.login(sessionToken, type, userId, signal)
    if (signal.aborted) return null
    this.finishInteractiveLogin(result, type)
    return result
  }

  private applyBaseAuthenticatedState(result: UserInfo, type: string): void {
    this.userInfo = { userId: result.id, ...result }
    this.aiModelsCreditsLastSyncedAt = Date.now()
    this.offerSession = {}
    this.subscriptionSelectionReady = false
    this.offerSubscription = null
    this.offerRefreshInFlight = null
    this.userId = result.id
    this.username = result.username
    this.platformType = type
    this.email = this.userInfo.email ? this.userInfo.email : ''
    this.new_user = result.new_user

    const adminUsers = ['yoad@bazaart.me', 'moti@bazaart.me', 'dror@bazaart.me','l.yadid@gmail.com']
    this.isStaff = result.is_staff ? true : adminUsers.indexOf(result.email) !== -1

    // Shared by the normal and post-login-failure recovery paths so the push
    // subscriber is always linked to the authenticated user.
    syncOneSignalUser({ id: result.id })
  }

  private async handleLoginSuccess(result: UserInfo, type: string): Promise<void> {
    const session = this.offerSession
    const stillOwned = () => session === this.offerSession && this.userId === result.id
    // Avoid cross-account location prefill leakage between consecutive sign-ins.
    LocationGeoIPService.shared.clearCache()
    LocationGeoIPService.shared.prefetchLocation()

    // TODO: second userId should be changed to name and en should be changed to dynamic langague
    let userDevice = new UserDevice(this.userId.toString(), this.username, type, "web", "web", (new Date()).getSeconds(), "en", undefined)
    userDevice.save();

    await SyncManager.shared.setFirebaseToken(result.firebase_token, result.username);
    if (!stillOwned()) return

    await this.refreshTransactionStatus()
    if (!stillOwned()) return

    const signUpSuccessPayload = buildSignUpSuccessProperties({
      context: this.getSignupAnalyticsContext(),
      destination: type,
    })

    customAmplitude('Sign up Success', {
      ...signUpSuccessPayload,
      isSandbox: process.env.REACT_APP_IS_SANDBOX,
      userId: result.id,
      email: result.email ? result.email : '',
      'Auth Flow': result.new_user ? 'SignUp' : 'SignIn',
    })

    const identifyEvent = new amplitude.Identify()
    identifyEvent.set('isSignup', true)
    amplitude.identify(identifyEvent)
    amplitude.setUserId(result.id.toString())
   
    Sentry.setUser({ id: result.id })
    initializeProfitWell(result.id)

    this.pushUserDataLoadedToDataLayer()

    sha256Hex(result.email).then(hash_email => {
      if (!stillOwned()) return
      const attribution = getAttributionContext()
      // @ts-ignore
      window.dataLayer.push({
        event: 'login',
        event_context: type,
        customer_id: result.id,
        user_email: hash_email,
        ...attribution,
      });

      if(result.new_user){
        // @ts-ignore
        window.dataLayer.push({
          event: 'sign_up',
          event_context: type,
          customer_id: result.id,
          user_email: hash_email,
          ...attribution,
        });
      }
      else{
        // @ts-ignore
        window.dataLayer.push({
          event: 'sign_in',
          event_context: type,
          customer_id: result.id,
          user_email: hash_email,
          ...attribution,
        });
      }
    });
  }

  private async refreshFirebaseToken() {
    if (this.didRefreshFirebaseToken) {
      return;
    }
    this.didRefreshFirebaseToken = true;

    try {
      const requestUserId = this.userId
      const requestUsername = this.username ?? undefined
      if (requestUserId == null) {
        this.didRefreshFirebaseToken = false;
        return
      }
      let firebase_token = await api.refreshFirebaseToken(requestUserId);
      if (this.userId !== requestUserId) {
        return
      }
      if (firebase_token) {
        await SyncManager.shared.setFirebaseToken(firebase_token, requestUsername);
      }
    } catch {
      this.didRefreshFirebaseToken = false;
    }
  }

  /**
   * Refreshes `/user/:id/` fields (credits, etc.). Server payload must not overwrite
   * JWT-derived subscription tier; stripped keys apply + JWT is re-applied before persist.
   */
  async refreshCurrentUserProfileFromServer(): Promise<boolean> {
    const requestUserId = this.userId
    if (!requestUserId) return false

    try {
      const latestUserData = (await ApiService.getUserById({ id: requestUserId })) as unknown as Record<
        string,
        unknown
      >
      if (this.userId !== requestUserId) return false

      const sanitizedProfile = { ...latestUserData }
      for (const key of USER_PROFILE_FIELDS_EXCLUDED_FROM_TOKEN_TIER) {
        delete sanitizedProfile[key]
      }
      this.userInfo = {
        ...this.userInfo,
        ...sanitizedProfile,
      }
      this.aiModelsCreditsLastSyncedAt = Date.now()
      this.applySubscriptionFlagsFromAccessToken()
      this.persistUserStateToStoreAndStorage()
      return true
    } catch (error) {
      console.error('Failed to refresh current user profile from server:', error)
      return false
    }
  }

  private persistUserStateToStoreAndStorage() {
    const userInfo = {
      ...this.userInfo,
      userId: this.userId,
      username: this.username,
      platformType: this.platformType,
      isStaff: this.isStaff,
      userIsPremium: this.userIsPremium,
      userIsSuper: this.userIsSuper,
      userHaveActivePaddleSubscription: this.userHaveActivePaddleSubscription,
      manageCancelUrl: this.manageCancelUrl,
      manageUpdateUrl: this.manageUpdateUrl,
      paddleWebSubscriptionId: this.paddleWebSubscriptionId,
      userHadAFreeTrial: this.userHadAFreeTrial,
      userPremiumOnPlatform: this.userPremiumOnPlatform,
    }
    delete userInfo['firebase_token']
    UserInfoStorage.set(userInfo)
    store.dispatch(setUser(this.getUserProperties()))
    store.dispatch(setUserIsPremium(!!this.userIsPremium))
    store.dispatch(setUserIsSuper(!!this.userIsSuper))
  }

  /**
   * Re-derives `userIsPremium` / `userIsSuper` from the current access token
   * JWT and pushes them to Redux + UserInfoStorage. Called from
   * `ApiService.refreshAccessToken` and the legacy `accessToken` exchange path
   * so that JWT tier changes (e.g. premium lapses to free mid-session) are
   * reflected in client gates immediately, not on next reload.
   *
   * No-op when the user is signed out:
   *   - The `ApiService.accessTokenRevision` guard prevents this method from
   *     being reached with a stale token while a logout is in flight.
   *   - `logout()` itself synchronously dispatches `userIsPremium=false`, so
   *     there is no "force off" work to do here.
   */
  public syncSubscriptionFlagsFromAccessToken(): void {
    if (!this.userId) return
    this.applySubscriptionFlagsFromAccessToken()
    this.persistUserStateToStoreAndStorage()
  }

  /**
   * Refreshes the user payload from server if the cached value is older than `maxAgeMs`,
   * and deduplicates concurrent callers via a single in-flight promise. Returns `true`
   * when the cached value is fresh (or just refreshed), `false` if the refresh failed.
   * Applies to all tiers because AI credits metadata is used in premium/super
   * out-of-credits flows as well.
   */
  async ensureAIModelsCreditsFresh(maxAgeMs: number): Promise<boolean> {
    if (!this.userId) return true

    const lastSyncedAt = this.aiModelsCreditsLastSyncedAt
    if (lastSyncedAt != null && Date.now() - lastSyncedAt < maxAgeMs) return true

    if (!this.aiModelsCreditsRefreshInFlight) {
      this.aiModelsCreditsRefreshInFlight = this.refreshCurrentUserProfileFromServer()
        .finally(() => { this.aiModelsCreditsRefreshInFlight = null })
    }
    return this.aiModelsCreditsRefreshInFlight
  }

  async refreshTransactionStatus(maxAttempts: number = 1): Promise<boolean> {
    const session = this.offerSession
    if (this.transactionRefreshInFlight?.session === session) {
      const current = this.transactionRefreshInFlight
      return maxAttempts > current.maxAttempts
        ? current.promise.then(() => session === this.offerSession ? this.refreshTransactionStatus(maxAttempts) : false)
        : current.promise
    }
    const promise = this.refreshTransactionStatusOnce(maxAttempts).finally(() => {
      if (this.transactionRefreshInFlight?.promise === promise) this.transactionRefreshInFlight = null
    })
    this.transactionRefreshInFlight = { session, maxAttempts, promise }
    return promise
  }

  private async refreshTransactionStatusOnce(maxAttempts: number): Promise<boolean> {
    const session = this.offerSession
    const userId = this.userId
    this.subscriptionSelectionReady = false
    // JWT is the canonical source for premium/super flags; transactions only
    // supply billing metadata. Non-premium users have no manage URLs / web
    // subscription id to fetch, so skip the heavier listTransactions probe
    // entirely and just reset any stale state from a previous premium session.
    this.applySubscriptionFlagsFromAccessToken()
    if (!this.userIsPremium) {
      this.manageCancelUrl = null
      this.manageUpdateUrl = null
      this.paddleWebSubscriptionId = null
      this.userPremiumOnPlatform = null
      this.userHaveActivePaddleSubscription = null
      this.userHadAFreeTrial = this.userInfo?.can_trial === false
      this.checkTransactionDone = true
      this.persistUserStateToStoreAndStorage()
      const identifyEvent = new amplitude.Identify()
      identifyEvent.set('isPremium', false)
      identifyEvent.set('isSuper', false)
      amplitude.identify(identifyEvent)
      return true
    }

    const statusOk = await this.checkTransactionStatus(maxAttempts)
    if (!statusOk || session !== this.offerSession || userId !== this.userId) {
      // If transaction status check failed, do not proceed
      return false;
    }
    this.subscriptionSelectionReady = true
    this.checkTransactionDone = true
    this.persistUserStateToStoreAndStorage()
    const identifyEvent = new amplitude.Identify()
    identifyEvent.set('isPremium', !!this.userIsPremium)
    identifyEvent.set('isSuper', !!this.userIsSuper)
    amplitude.identify(identifyEvent)
    return true;
  }

  private isWebPaddlePlatform(purchase: Record<string, unknown>): boolean {
    const p = String(purchase['platform'] || '').toLowerCase()
    return p === 'web' || p === 'paddle_billing'
  }

  private premiumPlatformFromPurchase(
    purchase?: Record<string, unknown> | null,
  ): PremiumOnPlatformType | null {
    const platform = String(purchase?.['platform'] || '').toLowerCase()
    if (!platform) return null
    if (platform === 'web' || platform === 'paddle_billing') return PremiumOnPlatformType.web
    if (
      platform === 'google' ||
      platform === 'google_play' ||
      platform === 'play_store' ||
      platform === 'android'
    ) {
      return PremiumOnPlatformType.android
    }
    if (platform === 'apple' || platform === 'ios' || platform === 'app_store') {
      return PremiumOnPlatformType.ios
    }
    return null
  }

  private webSubscriptionIdFromPurchase(purchase: Record<string, unknown>): string | null {
    const raw =
      (purchase['original_transaction_id'] as string | undefined) ||
      (purchase['originalTransactionId'] as string | undefined) ||
      (purchase['paddle_subscription_id'] as string | undefined)
    const s = raw ? String(raw).trim() : ''
    return s || null
  }

  /**
   * Polls `refreshAccessToken()` until the refreshed JWT exposes the purchased
   * tier. This is the single source of truth for subscription flags;
   * transactions only supply billing metadata (manage URLs, subscription id,
   * premium platform). Pass `needsSuper=true` when polling after a Super
   * checkout so a pre-existing `userIsPremium` doesn't cause an early return.
   */
  private async waitForPremiumToken(maxAttempts: number = 1, needsSuper: boolean = false): Promise<boolean> {
    const delayBetweenRequests = 500

    for (let attempts = 0; attempts < maxAttempts; attempts++) {
      try {
        await ApiService.refreshAccessToken()
      } catch {
        /* best-effort: keep polling */
      }
      this.applySubscriptionFlagsFromAccessToken()
      if (needsSuper ? this.userIsSuper : this.userIsPremium) {
        return true
      }

      if (attempts < maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, delayBetweenRequests))
      }
    }

    return false
  }

  private async checkTransactionStatus(maxAttempts: number = 1): Promise<boolean> {
    const session = this.offerSession
    const userId = this.userId
    const isUserPaying = (purchase: PurchaseRecord) => isPurchaseInEffect(purchase)


    let transactions = []
    let storeMetadata = new Map<number, SubscriptionStore>()
    let userIsAlreadyPaying = false
    let attempts = 0
    const delayBetweenRequests = 500
    let hadError = false;

    while (attempts < maxAttempts && !userIsAlreadyPaying) {
      try {
        const result = await ApiService.listTransactions()
        if (session !== this.offerSession || userId !== this.userId) return false
        if (result && result.objects) {
          transactions = result.objects
          storeMetadata = await this.loadPurchaseStoreMetadata(transactions.filter(isUserPaying))
          if (session !== this.offerSession || userId !== this.userId) return false
          if (result.objects.some(purchase => isUserPaying(purchase) && purchase.is_valid !== false && isSubscriptionPurchase(purchase, storeMetadata))) {
            userIsAlreadyPaying = true
          }
        }
      } catch (error) {
        console.error(`Purchase metadata refresh attempt ${attempts + 1} failed:`, error)
        hadError = true;
        break;
      }
      if (!userIsAlreadyPaying && attempts < maxAttempts - 1) await new Promise(resolve => setTimeout(resolve, delayBetweenRequests))
      attempts++
    }

    if (hadError || session !== this.offerSession || userId !== this.userId) {
      this.offerSubscription = null
      return false;
    }

    const activePurchases = transactions.filter(isUserPaying) as PurchaseRecord[]
    const payingTransactions = activePurchases.filter(purchase => purchase.is_valid !== false && isSubscriptionPurchase(purchase, storeMetadata))
    payingTransactions.sort((a, b) => {
      const da = a['purchase_date'] ? new Date(String(a['purchase_date'])).getTime() : 0
      const db = b['purchase_date'] ? new Date(String(b['purchase_date'])).getTime() : 0
      return db - da
    })

    const paddleWebPaying = payingTransactions
      .filter(
        (purchase) =>
          Boolean(purchase['paddle_update_url']) || this.isWebPaddlePlatform(purchase as Record<string, unknown>)
      ) as Record<string, unknown>[]

    paddleWebPaying.sort((a, b) => {
      const da = a['purchase_date'] ? new Date(String(a['purchase_date'])).getTime() : 0
      const db = b['purchase_date'] ? new Date(String(b['purchase_date'])).getTime() : 0
      return db - da
    })

    // Trial eligibility comes from the backend `can_trial` flag (carried on the
    // user payload), not from inferring against transaction history.
    const userHadAFreeTrial = this.userInfo?.can_trial === false

    this.applySubscriptionFlagsFromAccessToken()
    this.userHadAFreeTrial = userHadAFreeTrial

    if (userIsAlreadyPaying && paddleWebPaying.length > 0) {
      const lastPayingTransaction = paddleWebPaying[0]
      const updateUrl = lastPayingTransaction['paddle_update_url'] as string | undefined
      const cancelUrl = lastPayingTransaction['paddle_cancel_url'] as string | undefined
      if (updateUrl) {
        this.manageCancelUrl = cancelUrl ?? null
        this.manageUpdateUrl = updateUrl
      } else {
        this.manageCancelUrl = null
        this.manageUpdateUrl = null
      }
      this.paddleWebSubscriptionId = this.webSubscriptionIdFromPurchase(lastPayingTransaction)
      this.userPremiumOnPlatform = PremiumOnPlatformType.web
      // Reaching this branch means there is a paying, still-entitled web Paddle
      // transaction — the web equivalent of iOS `hasActiveWebSubscription()`
      // (mostRecentPayingPurchase is web & isUserPaying). Such users must switch
      // plans in place via pay() → updatePaddlePlan() rather than open a parallel
      // checkout. Do not gate this on `expiration == null`: active renewing Paddle
      // subscriptions carry a non-null `expiration` (current period end), so that
      // check would wrongly exclude them and break the in-place upgrade path.
      this.userHaveActivePaddleSubscription = true
    } else if (userIsAlreadyPaying) {
      const lastPayingTransaction = payingTransactions[0]
      this.manageCancelUrl = null
      this.manageUpdateUrl = null
      this.paddleWebSubscriptionId = null
      this.userPremiumOnPlatform = this.premiumPlatformFromPurchase(lastPayingTransaction)
      this.userHaveActivePaddleSubscription = false
    } else {
      this.manageCancelUrl = null
      this.manageUpdateUrl = null
      this.paddleWebSubscriptionId = null
      this.userPremiumOnPlatform = null
      this.userHaveActivePaddleSubscription = false
    }
    await this.refreshLimitedTimeOfferSubscription(activePurchases, this.paddleWebSubscriptionId, storeMetadata)
    return session === this.offerSession && userId === this.userId
  }

  /**
   * Fetches Paddle Billing manage URLs via GET /purchase/txid/<id>/.
   * @param overrideSubscriptionId optional id from Redux user when SignInManager state may be stale
   * @param maxAttempts number of polling attempts (default 1). Use a higher value
   *   in the post-checkout flow where the backend may not have ingested the
   *   Paddle webhook yet.
   */
  public async ensureWebManageUrlsLoaded(
    overrideSubscriptionId?: string | null,
    maxAttempts: number = 1,
  ): Promise<void> {
    if (this.manageUpdateUrl) {
      if (!this.manageCancelUrl) {
        this.manageCancelUrl = this.manageUpdateUrl
      }
      return
    }
    const id = String(overrideSubscriptionId ?? this.paddleWebSubscriptionId ?? '').trim()
    if (!id) {
      return
    }
    if (String(overrideSubscriptionId ?? '').trim() && !this.paddleWebSubscriptionId) {
      this.paddleWebSubscriptionId = id
    }
    const delayBetweenRequests = 500
    for (let attempts = 0; attempts < maxAttempts; attempts++) {
      try {
        const data = await ApiService.getPurchaseByTransactionId(id)
        const updateUrl = data.paddle_update_url ?? null
        if (updateUrl) {
          this.manageUpdateUrl = updateUrl
          this.manageCancelUrl =
            (data.paddle_cancel_url as string | null | undefined) ?? updateUrl
          this.persistUserStateToStoreAndStorage()
          return
        }
      } catch (e) {
        // Hard error (network, 5xx). Don't retry — bail and let the next
        // on-demand call try again.
        console.error('ensureWebManageUrlsLoaded failed', e)
        return
      }
      if (attempts < maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, delayBetweenRequests))
      }
    }
  }

  /** Reads JWT `subscription_level` / legacy `is_premium` once and assigns instance + tier flags (super ⇒ premium). */
  private applySubscriptionFlagsFromAccessToken(): void {
    const accessToken = localStorage.getItem('access_token') || this.userInfo?.access_token
    if (!accessToken) {
      this.userIsPremium = false
      this.userIsSuper = false
      return
    }

    try {
      const decoded = this.decodeJwtResponse(accessToken) as Record<string, unknown>
      let { userIsPremium, userIsSuper } = deriveSubscriptionFlagsFromAccessTokenPayload(decoded)
      if (userIsSuper) {
        userIsPremium = true
      }
      this.userIsPremium = userIsPremium
      this.userIsSuper = userIsSuper
    } catch (error) {
      console.error('Failed to decode access token subscription flags:', error)
      this.userIsPremium = false
      this.userIsSuper = false
    }
  }

  // thanks - https://stackoverflow.com/a/75856811/126015
  public decodeJwtResponse(token) {
    var base64Url = token.split('.')[1]
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    var jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join('')
    )

    return JSON.parse(jsonPayload)
  }

  private handleGooglePageReturn = () => {
    if (!this.callbacks['google'] || this.googleAuthInFlight) return
    // Mobile Google often uses a same-tab / sheet flow and never sets
    // googlePopup. Treat only a real closed popup as cancel; otherwise the
    // credential callback still arrives after this visibility/pageshow event.
    if (!this.googlePopup || !this.googlePopup.closed) return
    this.callbacks['google'](false)
  }

  private handleGoogleVisibilityReturn = () => {
    if (document.visibilityState === 'visible') {
      this.handleGooglePageReturn()
    }
  }

  private attachGoogleReturnWatchers() {
    this.detachGoogleReturnWatchers()
    if (typeof document === 'undefined') return
    document.addEventListener('visibilitychange', this.handleGoogleVisibilityReturn)
    window.addEventListener('pageshow', this.handleGooglePageReturn)
  }

  private detachGoogleReturnWatchers() {
    if (typeof document === 'undefined') return
    document.removeEventListener('visibilitychange', this.handleGoogleVisibilityReturn)
    window.removeEventListener('pageshow', this.handleGooglePageReturn)
  }

  private stopGoogleAttemptTracking() {
    if (this.googleCloseCheckInterval) {
      window.clearInterval(this.googleCloseCheckInterval)
      this.googleCloseCheckInterval = null
    }
    this.googlePopup = null
    this.detachGoogleReturnWatchers()
    if (this.googleOriginalOpen) {
      window.open = this.googleOriginalOpen
      this.googleOriginalOpen = null
    }
  }

  private createFakeGoogleWrapper = (container: HTMLDivElement) => {
    const state = nanoid()
    const googleLoginWrapper = document.createElement('div')

    // Must stay display:none. In a laid-out container GSI renders only a
    // cross-origin iframe, leaving no div[role=button] we can click.
    googleLoginWrapper.style.display = 'none'
    googleLoginWrapper.classList.add('custom-google-button')

    // Add the wrapper to body
    container.appendChild(googleLoginWrapper)

    // Use GSI javascript api to render the button inside our wrapper
    // You can ignore the properties because this button will not appear
    this.google?.accounts.id.renderButton(googleLoginWrapper, {
      type: 'icon',
      width: 200,
      state,
    })

    return {
      state,
      renew: () => this.createFakeGoogleWrapper(container),
      remove: () => googleLoginWrapper.remove(),
      click: () => {
        // GSI injects its internal button asynchronously. Looking it up during
        // setup races that render on slower mobile devices, so resolve it only
        // when the user actually taps our Google button.
        const googleLoginWrapperButton =
          googleLoginWrapper.querySelector('div[role=button]') ??
          googleLoginWrapper.querySelector('button')
        if (!(googleLoginWrapperButton instanceof HTMLElement)) {
          throw new Error('Google Sign-In button failed to render')
        }

        let that = this
        that.googleOriginalOpen = window.open
        window.open = (url, target, windowFeatures) => {
          const openedWindow = that.googleOriginalOpen?.call(window, url, target, windowFeatures) ?? null
          that.googlePopup = openedWindow

          if (!openedWindow) {
            that.callbacks['google']?.(
              false,
              new Error('Google Sign-In popup was blocked'),
            )
            return openedWindow
          }

          that.googleCloseCheckInterval = setInterval(() => {
            if (!that.callbacks['google']) {
              that.stopGoogleAttemptTracking()
              return
            }

            if (openedWindow.closed) {
              that.callbacks['google'](false)
            }
          }, 100)

          // GSI inspects the return value of window.open to decide whether the
          // popup was blocked. Safari (no FedCM) uses this popup path, so the
          // opened window MUST be returned; otherwise GSI sees `undefined`,
          // logs "Failed to open popup window", and aborts the sign-in.
          return openedWindow
        }
        // @ts-ignore
        googleLoginWrapperButton.click()
      },
    }
  }

  private commitAuthenticatedSession(result: UserInfo, type: string): void {
    this.applyBaseAuthenticatedState(result, type)
    this.applySubscriptionFlagsFromAccessToken()
    this.persistUserStateToStoreAndStorage()
    measureChatGPTRegistration(result.new_user === true, result.id)
  }

  // Interactive logins must become a persisted session as soon as /token/
  // succeeds. Firebase/SW setup can hang on mobile and must not be what
  // decides whether refresh still sees the user as signed in.
  private finishInteractiveLogin(result: UserInfo, type: string): void {
    this.commitAuthenticatedSession(result, type)
    void this.safeRunPostLoginSetup(result, type)
  }

  private recoverSessionStateAfterPostLoginSetupFailure(result: UserInfo, type: string): void {
    // Do not leave a redeemed session half-applied in the client. Even if
    // optional post-login setup fails, persist a consistent authenticated user
    // so route guards and modal flow don't bounce the user back to signup.
    this.applyBaseAuthenticatedState(result, type)
    this.applySubscriptionFlagsFromAccessToken()
    this.manageCancelUrl = null
    this.manageUpdateUrl = null
    this.paddleWebSubscriptionId = null
    this.userPremiumOnPlatform = null
    this.userHaveActivePaddleSubscription = null
    this.userHadAFreeTrial = this.userInfo?.can_trial === false
    this.checkTransactionDone = false
    this.persistUserStateToStoreAndStorage()

    // Recover billing metadata in background without blocking the login flow.
    void this.refreshTransactionStatus().catch((error) => {
      console.error('Deferred transaction status recovery failed:', error)
    })
  }

  // Runs handleLoginSuccess after a successful credential exchange. Failures here
  // (Firebase token, transaction status, analytics, etc.) must NOT be surfaced as
  // auth failures because the user is already authenticated on the server.
  private async safeRunPostLoginSetup(result: UserInfo, type: string): Promise<void> {
    const session = this.offerSession
    try {
      await this.handleLoginSuccess(result, type)
    } catch (loginSetupError) {
      if (session !== this.offerSession || this.userId !== result.id) return
      console.error('Post-login setup failed (user is authenticated):', loginSetupError)
      this.recoverSessionStateAfterPostLoginSetupFailure(result, type)
    }
  }

  // OTP Email verification - verifies the magic code and logs user in
  public async sendOTPEmail(code: string, email: string): Promise<UserInfo> {
    this.cancelPendingSocialLogin()
    // First, verify the OTP code - this can fail if code is invalid
    let result: UserInfo
    try {
      result = await ApiService.verifyOTPCode(email, code)
    } catch (err) {
      this.emitSignupFailed('Email', err, 'SignIn')
      throw err
    }

    this.finishInteractiveLogin(result, 'email')
    return this.getUserProperties()
  }

  /**
   * Cross-subdomain SSO redeem (design.bazaart.com). Exchanges the one-time
   * `sso_code` from photoshoot.bazaart.com for a fresh session, then runs the
   * normal post-login setup. If the redeemed account differs from the
   * currently-stored one (silent re-auth, no logout), clears the previous
   * user's drafts + signs Firebase out first — mirroring `logout()` cleanup so
   * the new user never inherits stale local sync state. `didRefreshFirebaseToken`
   * is held true across the cleanup to block any concurrent `getInstance()`
   * Firebase refresh from signing in as the outgoing user mid-cleanup.
   */
  public async completeSsoLogin(code: string): Promise<UserInfo> {
    captureAttributionFromSearch(window.location.search)
    this.setSignupAnalyticsContext(
      buildSignupAnalyticsContext({
        source: 'SSO',
        type: 'Standard',
        page: 'photoshoot',
      }),
    )

    const result = await ApiService.redeemSsoCode(code)
    await this.cleanupOnAccountSwitch(result.id)

    this.commitAuthenticatedSession(result, this.resolvePlatformTypeForSso(result))
    await this.safeRunPostLoginSetup(result, this.resolvePlatformTypeForSso(result))
    return this.getUserProperties()
  }

  private normalizeBzAuthUserInfo(raw: unknown): UserInfo | null {
    if (!raw || typeof raw !== 'object') return null
    const source = raw as Record<string, unknown>
    const rawId = source.id ?? source.userId
    let id: number | null = null
    if (typeof rawId === 'number' && Number.isFinite(rawId)) {
      id = rawId
    } else if (typeof rawId === 'string' && rawId.trim() !== '') {
      const parsed = Number(rawId)
      if (Number.isFinite(parsed)) id = parsed
    }
    if (id == null) return null
    return {
      ...source,
      id,
      userId: id,
    } as unknown as UserInfo
  }

  private async cleanupOnAccountSwitch(nextUserId: unknown): Promise<void> {
    const prevUserId = UserInfoStorage.get()?.userId ?? null
    if (prevUserId == null || nextUserId == null || String(prevUserId) === String(nextUserId)) {
      return
    }

    this.didRefreshFirebaseToken = true
    try {
      await SyncManager.shared.signOutFirebase()
    } catch (error) {
      console.error('SSO account-switch cleanup failed:', error)
    } finally {
      this.didRefreshFirebaseToken = false
    }
  }

  private resolvePlatformTypeForSso(result: UserInfo): string {
    const asRecord = result as unknown as Record<string, unknown>
    const storedType = UserInfoStorage.get()?.platformType
    if (typeof storedType === 'string' && storedType && storedType !== 'sso') {
      return storedType
    }

    const explicitPlatformType = asRecord.platformType
    if (typeof explicitPlatformType === 'string' && explicitPlatformType && explicitPlatformType !== 'sso') {
      return explicitPlatformType
    }

    const identityProvider = asRecord.identity_provider
    if (typeof identityProvider === 'string' && identityProvider) {
      return identityProvider.toLowerCase()
    }

    if (asRecord.google_id) return 'google'
    if (asRecord.facebook_id) return 'facebook'
    if (asRecord.apple_id) return 'apple'
    return 'email'
  }

  /**
   * Fallback for cross-subdomain handoff via `#bzAuth=...` hash payload.
   * Used when `/sso_redeem/` fails (expired/invalid one-time code) but sender
   * still provided a valid access token + user payload.
   * TODO(photoshoot-handoff): Revisit removal when redeem failures are no
   * longer observed in production-like environments.
   */
  public async completeBzAuthLogin(payload: unknown): Promise<UserInfo> {
    if (!payload || typeof payload !== 'object') {
      throw new Error('Invalid bzAuth payload')
    }
    const source = payload as Record<string, unknown>
    const accessToken = typeof source.access_token === 'string' ? source.access_token : ''
    const refreshToken = typeof source.refresh_token === 'string' ? source.refresh_token : ''
    const vendorId = typeof source.VendorId === 'string' ? source.VendorId : ''
    const normalizedUser = this.normalizeBzAuthUserInfo(source.userInfo)

    if (!accessToken || !normalizedUser || normalizedUser.id == null) {
      throw new Error('bzAuth payload missing required auth fields')
    }

    captureAttributionFromSearch(window.location.search)
    this.setSignupAnalyticsContext(
      buildSignupAnalyticsContext({
        source: 'SSO',
        type: 'Standard',
        page: 'photoshoot',
      }),
    )

    // Reset the in-memory token first so request interceptors re-read from
    // localStorage and never keep using a stale in-memory token.
    ApiService.clearAccessToken()
    localStorage.setItem('access_token', accessToken)
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken)
    if (vendorId) localStorage.setItem('VendorId', vendorId)

    const result: UserInfo = {
      ...normalizedUser,
      id: normalizedUser.id,
      userId: normalizedUser.userId ?? normalizedUser.id,
      access_token: accessToken,
      refresh_token:
        refreshToken ||
        (typeof normalizedUser.refresh_token === 'string' ? normalizedUser.refresh_token : ''),
      firebase_token:
        typeof normalizedUser.firebase_token === 'string' ? normalizedUser.firebase_token : '',
      new_user: typeof normalizedUser.new_user === 'boolean' ? normalizedUser.new_user : false,
    }

    await this.cleanupOnAccountSwitch(result.id)
    this.commitAuthenticatedSession(result, this.resolvePlatformTypeForSso(result))
    await this.safeRunPostLoginSetup(result, this.resolvePlatformTypeForSso(result))
    return this.getUserProperties()
  }

  // Request OTP code to be sent to email
  public async loginWithEmail(email: string): Promise<void> {
    this.cancelPendingSocialLogin()
    customAmplitude(
      'Sign up click',
      buildSignUpClickProperties({
        context: this.getSignupAnalyticsContext(),
        destination: 'Email',
      }),
    )
    await ApiService.sendMagicCode(email)
  }

  public async loginWithEmailAndPassword(email: string, password: string): Promise<UserInfo> {
    this.cancelPendingSocialLogin()
    customAmplitude(
      'Sign up click',
      buildSignUpClickProperties({
        context: this.getSignupAnalyticsContext(),
        destination: 'EmailPassword',
      }),
    )

    let result: UserInfo
    try {
      result = await ApiService.loginWithEmailPassword(email, password)
    } catch (err) {
      this.emitSignupFailed('EmailPassword', err, 'SignIn')
      throw err
    }

    this.finishInteractiveLogin(result, 'emailpassword')
    return this.getUserProperties()
  }

  public async signUpWithEmailAndPassword(email: string, password: string): Promise<void> {
    customAmplitude(
      'Sign up click',
      buildSignUpClickProperties({
        context: this.getSignupAnalyticsContext(),
        destination: 'EmailPasswordSignup',
      }),
    )

    try {
      const registered = await ApiService.registerWithEmailPassword(email, password)
      measureChatGPTRegistration(true, registered.id)
    } catch (err) {
      this.emitSignupFailed('EmailPasswordSignup', err, 'SignUp')
      throw err
    }
  }

}
